/* eslint-disable react-hooks/exhaustive-deps */
// Screen: "Game Table (main component)"
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./context/AuthContext";
import { connectSocket, sendGameState, leaveRoom } from "./socketClient";
import { getApiBaseUrl } from "./utils/platform";
import { hapticFeedback, hapticNotification } from "./utils/native";
import ChatPanel from "./components/ChatPanel";
import VoiceChat from "./components/VoiceChat";
import SpectatorOverlay from "./components/SpectatorOverlay";
import { AI_NAMES, AI_PASS_SAYINGS, AI_ORDER_SAYINGS, AI_NAMING_SAYINGS, AI_SIT_SAYINGS, AI_PLAY_SAYINGS } from "./config/aiConfig";
import EloBadge, { RANK_TIERS, getRankFromPoints, getRankProgress } from "./components/EloBadge";
import { sendTip, claimAdReward } from "./apiClient";

// ─── RESPONSIVE HOOK ─────────────────────────────────────────────────────────
function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}

function uuidv4() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const API_BASE_URL = getApiBaseUrl();
const SUITS = ["♠", "♥", "♦", "♣"];
const SUIT_COLORS = { "♠": "#0f172a", "♥": "#dc2626", "♦": "#dc2626", "♣": "#0f172a" };
// Hairline stroke on each suit for crispness + subtle tint to distinguish ♠ from ♣
const SUIT_STROKE = {
  "♠": "0.8px rgba(30,64,175,0.55)",   // blue-tinted edge
  "♣": "0.8px rgba(21,128,61,0.55)",   // green-tinted edge
  "♥": "0.6px rgba(185,28,28,0.4)",
  "♦": "0.6px rgba(185,28,28,0.4)",
};
const SUIT_PARTNER = { "♠": "♣", "♣": "♠", "♥": "♦", "♦": "♥" };

const RANKS_3P = ["10", "J", "Q", "K", "A"];
const RANKS_4P = ["9", "10", "J", "Q", "K", "A"];
// Combined rank order that works for both 3 and 4 player games
const RANK_ORDER = { "9": 0, "10": 1, J: 2, Q: 3, K: 4, A: 5 };

const GAME_START_SCORE = 21;

const DEAL_ANIM_MS = 130;
// eslint-disable-next-line no-unused-vars
const DEAL_BETWEEN_PASSES_MS = 550;
const TURNED_UP_REVEAL_DELAY_MS = 900;

const FIND_DEAL_CARD_MS = 350;
const FIND_DEAL_WIN_PAUSE_MS = 1000;



// ─── DECK & CARD LOGIC ────────────────────────────────────────────────────────
function buildDeck(playerCount = 3) {
  const deck = [];
  const ranks = playerCount === 4 ? RANKS_4P : RANKS_3P;
  for (const s of SUITS) for (const r of ranks) deck.push({ rank: r, suit: s });
  return deck;
}

function shuffle(arr) {
  let a = [...arr];
  // Triple-pass Fisher-Yates with rejection sampling to eliminate modulo bias.
  // Each pass uses fresh cryptographic randomness for maximum entropy.
  for (let pass = 0; pass < 3; pass++) {
    const randomValues = new Uint32Array(a.length);
    crypto.getRandomValues(randomValues);
    for (let i = a.length - 1; i > 0; i--) {
      // Rejection sampling: discard values that would cause modulo bias
      const range = i + 1;
      const limit = Math.floor(0x100000000 / range) * range; // largest multiple of range that fits in uint32
      let r = randomValues[i];
      if (r >= limit) {
        // Extremely rare (~0.000006% for 24 cards) — resample
        const extra = new Uint32Array(1);
        do { crypto.getRandomValues(extra); r = extra[0]; } while (r >= limit);
      }
      const j = r % range;
      [a[i], a[j]] = [a[j], a[i]];
    }
  }
  return a;
}

function cardKey(c) {
  if (!c) return "";
  return c.rank + c.suit;
}

function effectiveSuit(card, trump) {
  if (!trump) return card.suit;
  if (card.rank === "J" && card.suit === SUIT_PARTNER[trump]) return trump;
  return card.suit;
}

function isLeftBower(card, trump) {
  return card.rank === "J" && card.suit === SUIT_PARTNER[trump];
}

function isRightBower(card, trump) {
  return card.rank === "J" && card.suit === trump;
}

function cardStrength(card, trump, ledSuit) {
  if (isRightBower(card, trump)) return 100;
  if (isLeftBower(card, trump)) return 99;

  const eSuit = effectiveSuit(card, trump);
  const base = RANK_ORDER[card.rank];

  if (eSuit === trump) return 50 + base;
  if (eSuit === ledSuit) return 20 + base;
  return base;
}

// Stable sort for display: group by suit (trump last), then by rank within suit
function sortHandForDisplay(hand, trump) {
  if (!hand || hand.length === 0) return hand;
  const SUIT_ORDER = { "♠": 0, "♥": 1, "♦": 2, "♣": 3 };
  return [...hand].sort((a, b) => {
    const aEff = effectiveSuit(a, trump);
    const bEff = effectiveSuit(b, trump);
    // Trump cards go to the right
    const aIsTrump = aEff === trump ? 1 : 0;
    const bIsTrump = bEff === trump ? 1 : 0;
    if (aIsTrump !== bIsTrump) return aIsTrump - bIsTrump;
    // Within same trump/non-trump group, sort by effective suit
    const aSuit = SUIT_ORDER[aEff] ?? 0;
    const bSuit = SUIT_ORDER[bEff] ?? 0;
    if (aSuit !== bSuit) return aSuit - bSuit;
    // Within same suit, sort by rank
    return (RANK_ORDER[a.rank] ?? 0) - (RANK_ORDER[b.rank] ?? 0);
  });
}

function isBlackJack(card) {
  if (!card) return false;
  if (card.rank !== "J") return false;
  return card.suit === "♠" || card.suit === "♣";
}

// ─── AI LEARNING SYSTEM ───────────────────────────────────────────────────────
function createLearningModule() {
  const STORAGE_KEY = "buck_euchre_ai_learning";

  function getInsights() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return initializeInsights();
      return migrateInsights(JSON.parse(stored));
    } catch {
      return initializeInsights();
    }
  }

  function initializeInsights() {
    return {
      version: 2,
      gamesAnalyzed: 0,
      lastUpdated: new Date().toISOString(),
      trumpCalling: {
        withRightBower: { attempts: 0, successes: 0 },
        withLeftBower: { attempts: 0, successes: 0 },
        with3Trump: { attempts: 0, successes: 0 },
        with2Trump: { attempts: 0, successes: 0 },
        dealerPickup: { attempts: 0, successes: 0 },
        euchred: { attempts: 0, count: 0 },
      },
      playDecisions: {
        playWith1Trump: { attempts: 0, successes: 0 },
        playWith2Trump: { attempts: 0, successes: 0 },
        sitOut: { attempts: 0, losses: 0 },
      },
      cardPlay: {
        leadHighTrump: { attempts: 0, tricksWon: 0 },
        leadOffAce: { attempts: 0, tricksWon: 0 },
        economicalWins: { attempts: 0, successes: 0 },
      },
      patterns: {
        bestTrumpCallThreshold: 2,
        bestPlayThreshold: 1,
        aggressivenessMultiplier: 1.0,
      },
    };
  }

  function migrateInsights(insights) {
    if (!insights.version || insights.version < 2) {
      insights.version = 2;
      if (!insights.trumpCalling.euchred) {
        insights.trumpCalling.euchred = { attempts: 0, count: 0 };
      }
    }
    return insights;
  }

  function saveInsights(insights) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(insights));
    } catch (e) {
      console.warn("Failed to save AI learning data:", e);
    }
  }

  function analyzeGame(gameLog) {
    const insights = getInsights();
    insights.gamesAnalyzed++;
    insights.lastUpdated = new Date().toISOString();

    // Analyze trump calling decisions and outcomes
    const handCompletes = gameLog.filter((m) => m.move_type === "hand_complete");

    for (const handComplete of handCompletes) {
      const trumpCaller = handComplete.trump_caller;
      const trumpCallerSuccess = handComplete.score_delta[trumpCaller] < 0; // Negative = won points

      // Find the trump call for this hand
      const handStart = gameLog.findIndex((m) => m.move_type === "hand_deal" && m.hand_number === handComplete.hand_number);
      if (handStart === -1) continue;

      const handMoves = gameLog.filter(
        (m) => m.timestamp >= gameLog[handStart].timestamp && m.timestamp <= handComplete.timestamp
      );

      const trumpCall = handMoves.find(
        (m) => (m.move_type === "trump_round1" || m.move_type === "trump_round2") && m.player_id === trumpCaller
      );

      if (!trumpCall) continue;

      // Analyze what led to success/failure
      const dealInfo = gameLog[handStart];
      const callerHand = dealInfo.hands_dealt[`p${trumpCaller}`] || [];

      // Count trump in original hand
      const trump = handComplete.trump;
      let trumpCount = 0;
      let hasRB = false;
      let hasLB = false;

      for (const cardKey of callerHand) {
        const rank = cardKey[0];
        const suit = cardKey.slice(1);
        if (suit === trump) {
          trumpCount++;
          if (rank === "J") hasRB = true;
        }
        if (rank === "J" && SUIT_PARTNER[suit] === trump) {
          hasLB = true;
          trumpCount++;
        }
      }

      // Update success rates
      if (hasRB) {
        insights.trumpCalling.withRightBower.attempts++;
        if (trumpCallerSuccess) insights.trumpCalling.withRightBower.successes++;
      }
      if (hasLB) {
        insights.trumpCalling.withLeftBower.attempts++;
        if (trumpCallerSuccess) insights.trumpCalling.withLeftBower.successes++;
      }
      if (trumpCount >= 3) {
        insights.trumpCalling.with3Trump.attempts++;
        if (trumpCallerSuccess) insights.trumpCalling.with3Trump.successes++;
      } else if (trumpCount >= 2) {
        insights.trumpCalling.with2Trump.attempts++;
        if (trumpCallerSuccess) insights.trumpCalling.with2Trump.successes++;
      }

      // Track euchres (caller got 0 tricks = euchred)
      insights.trumpCalling.euchred.attempts++;
      const callerTricksWon = handComplete.tricks_won[trumpCaller] || 0;
      if (callerTricksWon === 0) {
        insights.trumpCalling.euchred.count++;
      }

      // Track play/sit decisions
      const playDecisions = handMoves.filter((m) => m.move_type === "decide_play");
      for (const pd of playDecisions) {
        if (pd.player_id === trumpCaller) continue; // Skip trump caller

        const playerSuccess = handComplete.score_delta[pd.player_id] < 0;

        if (pd.will_play) {
          const playerHand = dealInfo.hands_dealt[`p${pd.player_id}`] || [];
          let playerTrumpCount = 0;
          for (const cardKey of playerHand) {
            const rank = cardKey[0];
            const suit = cardKey.slice(1);
            if (suit === trump || (rank === "J" && SUIT_PARTNER[suit] === trump)) {
              playerTrumpCount++;
            }
          }

          if (playerTrumpCount === 1) {
            insights.playDecisions.playWith1Trump.attempts++;
            if (playerSuccess) insights.playDecisions.playWith1Trump.successes++;
          } else if (playerTrumpCount >= 2) {
            insights.playDecisions.playWith2Trump.attempts++;
            if (playerSuccess) insights.playDecisions.playWith2Trump.successes++;
          }
        } else {
          insights.playDecisions.sitOut.attempts++;
          if (!playerSuccess) insights.playDecisions.sitOut.losses++;
        }
      }
    }

    // Update pattern thresholds based on success rates
    updatePatterns(insights);
    saveInsights(insights);

    return insights;
  }

  function updatePatterns(insights) {
    // Check euchre rate first — if getting euchred too often, pull back hard
    const euchreRate = insights.trumpCalling.euchred.attempts > 10
      ? insights.trumpCalling.euchred.count / insights.trumpCalling.euchred.attempts
      : 0;

    if (euchreRate > 0.3 && insights.trumpCalling.euchred.attempts > 10) {
      // Getting euchred >30% of the time — very conservative
      insights.patterns.bestTrumpCallThreshold = 3;
      insights.patterns.aggressivenessMultiplier = 0.5;
    } else {
      // Adjust trump calling threshold based on success rates
      const with2TrumpRate =
        insights.trumpCalling.with2Trump.attempts > 10
          ? insights.trumpCalling.with2Trump.successes / insights.trumpCalling.with2Trump.attempts
          : 0.5;

      if (with2TrumpRate > 0.6 && insights.trumpCalling.with2Trump.attempts > 20) {
        insights.patterns.bestTrumpCallThreshold = 2;
        insights.patterns.aggressivenessMultiplier = 1.5;
      } else if (with2TrumpRate < 0.4 && insights.trumpCalling.with2Trump.attempts > 20) {
        insights.patterns.bestTrumpCallThreshold = 3;
        insights.patterns.aggressivenessMultiplier = 0.5;
      } else {
        insights.patterns.bestTrumpCallThreshold = 2;
        insights.patterns.aggressivenessMultiplier = 1.0;
      }
    }

    // Adjust play threshold based on play/sit success rates
    const play1TrumpRate =
      insights.playDecisions.playWith1Trump.attempts > 10
        ? insights.playDecisions.playWith1Trump.successes / insights.playDecisions.playWith1Trump.attempts
        : 0.5;

    if (play1TrumpRate > 0.55 && insights.playDecisions.playWith1Trump.attempts > 15) {
      insights.patterns.bestPlayThreshold = 1;
    } else if (play1TrumpRate < 0.35 && insights.playDecisions.playWith1Trump.attempts > 15) {
      insights.patterns.bestPlayThreshold = 2;
    } else {
      insights.patterns.bestPlayThreshold = 1;
    }
  }

  let serverParams = null;

  function setServerParams(params) {
    serverParams = params;
  }

  function getInsightsWithServer() {
    const insights = getInsights();
    if (serverParams) {
      insights.patterns.bestTrumpCallThreshold = serverParams.bestTrumpCallThreshold;
      insights.patterns.bestPlayThreshold = serverParams.bestPlayThreshold;
      insights.patterns.aggressivenessMultiplier = serverParams.aggressivenessMultiplier;
      // v2 params
      if (serverParams.seatMultipliers) insights.patterns.seatMultipliers = serverParams.seatMultipliers;
      if (serverParams.nextSuitBonus !== undefined) insights.patterns.nextSuitBonus = serverParams.nextSuitBonus;
      if (serverParams.conservativeScoreThreshold !== undefined) insights.patterns.conservativeScoreThreshold = serverParams.conservativeScoreThreshold;
      if (serverParams.aggressiveScoreThreshold !== undefined) insights.patterns.aggressiveScoreThreshold = serverParams.aggressiveScoreThreshold;
      if (serverParams.voidBonus !== undefined) insights.patterns.voidBonus = serverParams.voidBonus;
      if (serverParams.callerSeatAdjust !== undefined) insights.patterns.callerSeatAdjust = serverParams.callerSeatAdjust;
      if (serverParams.leadTrumpRate !== undefined) insights.patterns.leadTrumpRate = serverParams.leadTrumpRate;
    }
    return insights;
  }

  return {
    getInsights: getInsightsWithServer,
    analyzeGame,
    saveInsights,
    setServerParams,
  };
}

const learningModule = createLearningModule();

// ─── HANDSTATE: INTRA-HAND CARD TRACKING ─────────────────────────────────────

function createHandState(playerCount, trump, playingPlayers) {
  const tricksWon = {};
  for (const p of playingPlayers) tricksWon[p] = 0;

  // Build the full set of trump cards in existence so we can track what's outstanding
  const trumpNonBowerRanks = playerCount === 4 ? ['A', 'K', 'Q', '10', '9'] : ['A', 'K', 'Q', '10'];
  const outstandingTrump = new Set();
  outstandingTrump.add(`J${trump}`);                  // Right bower
  outstandingTrump.add(`J${SUIT_PARTNER[trump]}`);    // Left bower
  for (const rank of trumpNonBowerRanks) outstandingTrump.add(`${rank}${trump}`);

  return {
    playedCards: [],
    trumpPlayed: 0,
    trumpRemaining: playerCount === 4 ? 7 : 6,
    bowersPlayed: { right: false, left: false },
    outstandingTrump,   // Set of card-key strings for trump not yet played
    playerVoids: {},
    tricksWon,
    playingPlayers,
    trickNumber: 0,
  };
}

function updateHandState(state, trick, trump, winner) {
  const newState = {
    ...state,
    playedCards: [...state.playedCards],
    bowersPlayed: { ...state.bowersPlayed },
    playerVoids: { ...state.playerVoids },
    tricksWon: { ...state.tricksWon },
    outstandingTrump: state.outstandingTrump ? new Set(state.outstandingTrump) : null,
  };

  const ledSuit = effectiveSuit(trick[0].card, trump);

  for (const t of trick) {
    newState.playedCards.push(t.card);
    const eSuit = effectiveSuit(t.card, trump);

    // Remove from outstandingTrump using rank+suit as key
    if (newState.outstandingTrump) {
      newState.outstandingTrump.delete(`${t.card.rank}${t.card.suit}`);
    }

    if (eSuit === trump) {
      newState.trumpPlayed++;
      newState.trumpRemaining--;
      if (isRightBower(t.card, trump)) newState.bowersPlayed.right = true;
      if (isLeftBower(t.card, trump)) newState.bowersPlayed.left = true;
    }

    if (t.player !== trick[0].player && eSuit !== ledSuit) {
      if (!newState.playerVoids[t.player]) newState.playerVoids[t.player] = {};
      newState.playerVoids[t.player] = { ...newState.playerVoids[t.player], [ledSuit]: true };
    }
  }

  if (winner !== undefined) {
    newState.tricksWon[winner] = (newState.tricksWon[winner] || 0) + 1;
  }
  newState.trickNumber++;
  return newState;
}

// ─── AI HELPERS ─────────────────────────────────────────────────────────────

function getSeatPositionFn(playerIdx, dealerIdx, playerCount) {
  const np = playerCount === 4 ? (p) => (p + 1) % 4 : (p) => (p + 2) % 3;
  let pos = 0;
  let current = np(dealerIdx);
  while (current !== playerIdx && pos < playerCount) {
    pos++;
    current = np(current);
  }
  return pos;
}

function getScoreAggressionFn(playerIdx, scores, params) {
  if (!scores || !scores.length) return 1.0;
  const myScore = scores[playerIdx];
  if (myScore <= (params.conservativeScoreThreshold || 5)) return 0.7;
  if (myScore >= (params.aggressiveScoreThreshold || 12)) return 1.3;
  if (scores.some((s, i) => i !== playerIdx && s <= 3)) return 1.2;
  return 1.0;
}

function getSeatMultiplierFn(seatPos, playerCount, params) {
  const mults = params.seatMultipliers || {};
  if (seatPos === playerCount - 1) return mults.dealer || 1.4;
  return mults[`seat${seatPos}`] || (seatPos === 1 ? 1.0 : 0.7);
}

function getAIParams() {
  const insights = learningModule.getInsights();
  return {
    bestTrumpCallThreshold: insights.patterns.bestTrumpCallThreshold || 2,
    bestPlayThreshold: insights.patterns.bestPlayThreshold || 1,
    aggressivenessMultiplier: insights.patterns.aggressivenessMultiplier || 1.0,
    seatMultipliers: insights.patterns.seatMultipliers || { seat0: 0.7, seat1: 1.0, seat2: 0.7, dealer: 1.4 },
    nextSuitBonus: insights.patterns.nextSuitBonus || 30,
    conservativeScoreThreshold: insights.patterns.conservativeScoreThreshold || 5,
    aggressiveScoreThreshold: insights.patterns.aggressiveScoreThreshold || 12,
    voidBonus: insights.patterns.voidBonus || 20,
    callerSeatAdjust: insights.patterns.callerSeatAdjust !== false,
    leadTrumpRate: insights.patterns.leadTrumpRate || 0.8,
  };
}

// ─── AI LOGIC (WITH LEARNING) — v2: seat, score, handState aware ─────────────
function aiTrumpDecisionRound1(hand, turnedUp, playerIdx, dealerIdx, overrideAggression, playerCount, scores) {
  const params = getAIParams();
  const minThreshold = params.bestTrumpCallThreshold;
  let aggression = overrideAggression || params.aggressivenessMultiplier;

  // Apply seat-position multiplier
  const seatPos = getSeatPositionFn(playerIdx, dealerIdx, playerCount || 3);
  const seatMult = getSeatMultiplierFn(seatPos, playerCount || 3, params);
  aggression *= seatMult;

  // Apply score awareness
  const scoreAgg = getScoreAggressionFn(playerIdx, scores, params);
  aggression *= scoreAgg;

  const potentialTrump = turnedUp.suit;
  let trumpCount = 0;
  let trumpStrength = 0;

  for (const c of hand) {
    if (effectiveSuit(c, potentialTrump) === potentialTrump) {
      trumpCount++;
      trumpStrength += cardStrength(c, potentialTrump, potentialTrump);
    }
  }

  const hasRB = hand.some((c) => isRightBower(c, potentialTrump));
  const hasLB = hand.some((c) => isLeftBower(c, potentialTrump));
  const hasAce = hand.some((c) => c.rank === "A" && effectiveSuit(c, potentialTrump) === potentialTrump);
  const isDealer = playerIdx === dealerIdx;
  const accept = () => (isDealer ? "pick_up" : "order_up");

  // Off-suit aces and voids add significant calling value
  const offAces = hand.filter(c => c.rank === 'A' && effectiveSuit(c, potentialTrump) !== potentialTrump).length;
  const nonTrumpSuits = SUITS.filter(s => s !== potentialTrump);
  const singletonVoids = nonTrumpSuits.filter(s =>
    hand.filter(c => effectiveSuit(c, potentialTrump) === s).length === 1
  ).length;
  // Each off-ace ≈ 15 strength pts; singleton suits (discard creates void) ≈ 8 pts each
  const effectiveTrumpStrength = trumpStrength + offAces * 15 + singletonVoids * 8;

  // Strong hands: Always accept
  if (hasRB && hasLB) return accept();
  if (hasRB && trumpCount >= 3) return accept();
  if (hasRB && hasAce) return accept();
  if (hasRB && offAces >= 1) return accept();  // RB + off-ace = very strong
  if (trumpCount >= 4) return accept();

  // Good hands: Accept based on position (adjusted by learning + seat + score)
  if (hasRB && trumpCount >= Math.max(2, minThreshold)) return accept();
  if (hasLB && trumpCount >= 3) return accept();
  if (trumpCount >= 3 && effectiveTrumpStrength > 150 / aggression) return accept();
  if (trumpCount >= 2 && offAces >= 2 && effectiveTrumpStrength > 100 / aggression) return accept(); // 2 trump + 2 off-aces

  // Dealer advantage (already boosted by seatMultiplier)
  if (isDealer) {
    if (trumpCount >= Math.max(2, minThreshold)) return accept();
    if (hasRB || hasLB) return accept();
    if (trumpCount >= 1 && offAces >= 2) return accept(); // dealer with 1 trump + 2 off-aces
  }

  // Position play: First seat requires 3 trump minimum (or 2 trump + 2 off-aces)
  if (seatPos === 0 && trumpCount >= 3) return accept();
  if (seatPos === 0 && trumpCount >= 2 && offAces >= 2) return accept();

  return "pass";
}

function aiTrumpDecisionRound2(hand, turnedUpSuit, playerIdx, dealerIdx, overrideAggression, playerCount, scores) {
  const isDealer = playerIdx === dealerIdx;
  const params = getAIParams();
  let aggression = overrideAggression || params.aggressivenessMultiplier;

  // Apply seat-position multiplier
  const seatPos = getSeatPositionFn(playerIdx, dealerIdx, playerCount || 3);
  const seatMult = getSeatMultiplierFn(seatPos, playerCount || 3, params);
  aggression *= seatMult;

  // Apply score awareness
  const scoreAgg = getScoreAggressionFn(playerIdx, scores, params);
  aggression *= scoreAgg;

  // "Next" suit = same color as turned-down card
  const nextSuit = SUIT_PARTNER[turnedUpSuit];
  const nextBonus = params.nextSuitBonus || 30;

  let bestSuit = null;
  let bestScore = -1;
  let bestCount = 0;
  let bestStrength = 0;

  for (const s of SUITS) {
    if (s === turnedUpSuit) continue;
    let count = 0;
    let strength = 0;

    for (const c of hand) {
      if (effectiveSuit(c, s) === s) {
        count++;
        strength += cardStrength(c, s, s);
      }
    }

    let score = count * 30 + strength;
    if (s === nextSuit) score += nextBonus;

    if (score > bestScore) {
      bestScore = score;
      bestCount = count;
      bestStrength = strength;
      bestSuit = s;
    }
  }

  const hasRB = bestSuit && hand.some((c) => isRightBower(c, bestSuit));
  const hasLB = bestSuit && hand.some((c) => isLeftBower(c, bestSuit));

  // "Stick the dealer" — dealer CAN pass; if all pass, hand is re-dealt
  if (isDealer) {
    if (bestCount >= 2 || hasRB || hasLB) return bestSuit;
    if (bestCount >= 1 && bestStrength > 60 / aggression) return bestSuit;
    return "pass";
  }

  // Non-dealer requires a good hand to call in round 2
  if (hasRB && bestCount >= 2) return bestSuit;
  if (hasLB && bestCount >= 3) return bestSuit;
  if (bestCount >= 3 && bestStrength > 120 / aggression) return bestSuit;
  if (bestCount >= 4) return bestSuit;

  return "pass";
}

function aiDecideToPlay(hand, trump, overrideAggression, scores, playerIdx, trumpCallerIdx, callerSeatPos, alreadyDecided, playerCount) {
  const params = getAIParams();
  const minPlayThreshold = params.bestPlayThreshold;
  let aggression = overrideAggression || params.aggressivenessMultiplier;

  // Apply score awareness
  const scoreAgg = getScoreAggressionFn(playerIdx, scores, params);
  aggression *= scoreAgg;

  let trumpCount = 0;
  let trumpStrength = 0;
  let offAces = 0;

  for (const c of hand) {
    if (effectiveSuit(c, trump) === trump) {
      trumpCount++;
      trumpStrength += cardStrength(c, trump, trump);
    } else if (c.rank === "A") {
      offAces++;
    }
  }

  const hasRB = hand.some((c) => isRightBower(c, trump));
  const hasLB = hand.some((c) => isLeftBower(c, trump));

  // Count void suits
  const possibleNonTrumpSuits = SUITS.filter(s => s !== trump);
  const voidCount = possibleNonTrumpSuits.filter(s => {
    return !hand.some(c => effectiveSuit(c, trump) === s);
  }).length;

  // Void bonus
  const voidBonusVal = (params.voidBonus || 20) * voidCount;
  const effectiveStrength = trumpStrength + voidBonusVal;

  // Caller seat adjustment
  let callerDanger = 1.0;
  if (params.callerSeatAdjust && callerSeatPos !== undefined) {
    if (callerSeatPos === 0) callerDanger = 0.85;
    if (callerSeatPos >= 2) callerDanger = 1.15;
  }
  aggression *= callerDanger;

  // Competition adjustment
  if (alreadyDecided && alreadyDecided.sitting >= 1) aggression *= 1.1;
  if (alreadyDecided && alreadyDecided.sitting >= 2) aggression *= 1.1;

  // Player count adjustment
  const is4P = (playerCount || 3) === 4;
  const alreadyPlaying = alreadyDecided ? alreadyDecided.playing + 1 : 1;

  if (is4P) {
    if (alreadyPlaying >= 2) aggression *= 0.5;
    else aggression *= 0.7;
  }

  // Strong hands: Always play
  if (hasRB) return true;
  if (hasLB && trumpCount >= 2) return true;
  if (trumpCount >= 3) return true;

  // 4P: require much stronger hands
  if (is4P) {
    if (trumpCount >= 2 && effectiveStrength > 120 / aggression) return true;
    if (trumpCount >= 2 && offAces >= 1) return Math.random() > 0.3 / aggression;
    if (trumpCount >= 2) return Math.random() > 0.5 / aggression;
    if (trumpCount >= 1 && voidCount >= 2) return Math.random() > 0.7 / aggression;
    return false;
  }

  // 3P: original thresholds
  if (trumpCount >= 2 && effectiveStrength > 100 / aggression) return true;
  if (trumpCount >= 1 && offAces >= 2) return true;

  if (trumpCount >= 2) return Math.random() > 0.2 / aggression;
  if (trumpCount >= minPlayThreshold && offAces >= 1) return Math.random() > 0.3 / aggression;
  if (trumpCount >= minPlayThreshold) return Math.random() > 0.5 / aggression;
  if (voidCount >= 2 && trumpCount >= 1) return Math.random() > 0.4 / aggression;

  if (offAces >= 2) return Math.random() > 0.7 / aggression;
  return Math.random() > 0.85;
}

function aiDiscard(hand, trump) {
  const nonTrump = hand.filter((c) => effectiveSuit(c, trump) !== trump);
  const trumpCards = hand.filter((c) => effectiveSuit(c, trump) === trump);

  if (nonTrump.length > 0) {
    // Strategic: create a void if possible by discarding a singleton non-ace suit
    const suitCounts = {};
    for (const c of nonTrump) {
      const s = effectiveSuit(c, trump);
      suitCounts[s] = (suitCounts[s] || 0) + 1;
    }
    // Singleton non-ace cards → discarding creates a void (future ruffing power)
    const voidCandidates = nonTrump.filter(c => {
      const s = effectiveSuit(c, trump);
      return suitCounts[s] === 1 && c.rank !== 'A';
    });
    if (voidCandidates.length > 0) {
      voidCandidates.sort((a, b) => RANK_ORDER[a.rank] - RANK_ORDER[b.rank]);
      return voidCandidates[0];
    }

    // No void to create — discard weakest non-trump non-ace
    const nonTrumpNonAce = nonTrump.filter((c) => c.rank !== "A");
    if (nonTrumpNonAce.length > 0) {
      nonTrumpNonAce.sort((a, b) => RANK_ORDER[a.rank] - RANK_ORDER[b.rank]);
      return nonTrumpNonAce[0];
    }
    // Only off-aces left in non-trump — discard lowest
    nonTrump.sort((a, b) => RANK_ORDER[a.rank] - RANK_ORDER[b.rank]);
    return nonTrump[0];
  }

  // Only trump — preserve bowers, discard weakest non-bower trump
  const nonBowers = trumpCards.filter((c) => !isRightBower(c, trump) && !isLeftBower(c, trump));
  if (nonBowers.length > 0) {
    nonBowers.sort((a, b) => cardStrength(a, trump, trump) - cardStrength(b, trump, trump));
    return nonBowers[0];
  }

  const leftBower = trumpCards.find((c) => isLeftBower(c, trump));
  if (leftBower) return leftBower;

  const sorted = [...hand].sort((a, b) => cardStrength(a, trump, trump) - cardStrength(b, trump, trump));
  return sorted[0];
}

function aiPlayCard(hand, trick, trump, trumpCallerIdx, currentPlayerIdx, handState) {
  if (!hand || hand.length === 0) return null;

  const params = handState ? getAIParams() : null;
  const ledSuit = trick.length > 0 ? effectiveSuit(trick[0].card, trump) : null;
  const followable = ledSuit ? hand.filter((c) => effectiveSuit(c, trump) === ledSuit) : hand;
  const playable = followable.length > 0 ? followable : hand;

  // Leading the trick
  if (trick.length === 0) {
    const trumpCards = playable.filter((c) => effectiveSuit(c, trump) === trump);
    const offSuitCards = playable.filter((c) => effectiveSuit(c, trump) !== trump);
    const offAces = playable.filter((c) => c.rank === "A" && effectiveSuit(c, trump) !== trump);
    const isTrumpCaller = (currentPlayerIdx === trumpCallerIdx);
    const hasRightBower = trumpCards.some(c => isRightBower(c, trump));

    // ─── HANDSTATE-AWARE LEADING ────────────────────────────────────────
    if (handState) {
      if (handState.trumpRemaining === 0 || (handState.trumpRemaining <= trumpCards.length)) {
        if (offAces.length > 0) {
          offAces.sort((a, b) => cardStrength(b, trump, null) - cardStrength(a, trump, null));
          return offAces[0];
        }
        if (offSuitCards.length > 0) {
          offSuitCards.sort((a, b) => RANK_ORDER[b.rank] - RANK_ORDER[a.rank]);
          return offSuitCards[0];
        }
      }

      const opponentTrumpEstimate = handState.trumpRemaining - trumpCards.length;
      if (opponentTrumpEstimate > 0 && offSuitCards.length > 0) {
        const safeSuits = offSuitCards.filter(c => {
          const suit = effectiveSuit(c, trump);
          const anyOpponentVoid = handState.playingPlayers.some(opp => {
            if (opp === currentPlayerIdx) return false;
            const voids = handState.playerVoids[opp];
            return voids && voids[suit];
          });
          return !anyOpponentVoid;
        });

        if (safeSuits.length > 0 && !isTrumpCaller) {
          safeSuits.sort((a, b) => RANK_ORDER[b.rank] - RANK_ORDER[a.rank]);
          return safeSuits[0];
        }
      }

      if (isTrumpCaller && trumpCards.length > 0 && opponentTrumpEstimate > 0) {
        // Use outstandingTrump to decide whether leading trump is safe
        if (handState.outstandingTrump && handState.outstandingTrump.size > 0) {
          // Find the highest outstanding trump card
          let highestOutstandingStrength = -1;
          for (const key of handState.outstandingTrump) {
            const suit = key.slice(-1);
            const rank = key.slice(0, -1);
            const s = cardStrength({ rank, suit }, trump, trump);
            if (s > highestOutstandingStrength) highestOutstandingStrength = s;
          }
          // Find our best trump
          trumpCards.sort((a, b) => cardStrength(b, trump, trump) - cardStrength(a, trump, trump));
          const myBestTrumpStrength = cardStrength(trumpCards[0], trump, trump);
          if (myBestTrumpStrength >= highestOutstandingStrength) {
            // We hold the best outstanding trump — lead it confidently to extract opponent trump
            return trumpCards[0];
          }
          // We don't hold the top trump — avoid leading into it; prefer off-suit plays
          if (offAces.length > 0) {
            offAces.sort((a, b) => cardStrength(b, trump, null) - cardStrength(a, trump, null));
            return offAces[0];
          }
          if (offSuitCards.length > 0) {
            offSuitCards.sort((a, b) => RANK_ORDER[b.rank] - RANK_ORDER[a.rank]);
            return offSuitCards[0];
          }
        }
        // Fallback: use leadRate
        const leadRate = (params && params.leadTrumpRate) || 0.8;
        if (Math.random() < leadRate) {
          trumpCards.sort((a, b) => cardStrength(b, trump, trump) - cardStrength(a, trump, trump));
          return trumpCards[0];
        }
      }
    }

    // ─── ORIGINAL LEADING LOGIC (fallback / no handState) ────────────────

    if (!isTrumpCaller && !hasRightBower && trumpCards.length < 4) {
      if (offAces.length > 0) {
        offAces.sort((a, b) => cardStrength(b, trump, null) - cardStrength(a, trump, null));
        return offAces[0];
      }
      if (offSuitCards.length > 0) {
        offSuitCards.sort((a, b) => cardStrength(b, trump, null) - cardStrength(a, trump, null));
        return offSuitCards[0];
      }
      trumpCards.sort((a, b) => cardStrength(a, trump, trump) - cardStrength(b, trump, trump));
      return trumpCards[0];
    }

    if (hasRightBower) {
      return trumpCards.find(c => isRightBower(c, trump));
    }

    if (trumpCards.length >= 3) {
      trumpCards.sort((a, b) => cardStrength(b, trump, trump) - cardStrength(a, trump, trump));
      return trumpCards[0];
    }

    if (offAces.length > 0 && trumpCards.length <= 1) {
      offAces.sort((a, b) => cardStrength(b, trump, null) - cardStrength(a, trump, null));
      return offAces[0];
    }

    if (trumpCards.length > 0 && trumpCards.length <= 2) {
      trumpCards.sort((a, b) => cardStrength(a, trump, trump) - cardStrength(b, trump, trump));
      const midIdx = Math.floor(trumpCards.length / 2);
      return trumpCards[midIdx];
    }

    playable.sort((a, b) => cardStrength(b, trump, null) - cardStrength(a, trump, null));
    return playable[0];
  }

  // Following the trick
  let bestStrength = -1;
  for (const t of trick) {
    const s = cardStrength(t.card, trump, ledSuit);
    if (s > bestStrength) bestStrength = s;
  }

  const canBeat = playable.filter((c) => cardStrength(c, trump, ledSuit) > bestStrength);

  // Survival mode: non-caller with 0 tricks and only 1-2 tricks remaining → must win at least 1
  // (playing and getting 0 tricks = +5 penalty)
  const iAmNotCaller = currentPlayerIdx !== trumpCallerIdx;
  const myTricksWon = handState ? (handState.tricksWon[currentPlayerIdx] || 0) : -1;
  const tricksLeft = handState ? Math.max(0, 5 - handState.trickNumber) : 5;
  const survivalMode = handState && iAmNotCaller && myTricksWon === 0 && tricksLeft <= 2;

  if (canBeat.length > 0) {
    if (survivalMode) {
      // Survival: play highest winning card to guarantee taking this trick
      canBeat.sort((a, b) => cardStrength(b, trump, ledSuit) - cardStrength(a, trump, ledSuit));
      return canBeat[0];
    }
    canBeat.sort((a, b) => cardStrength(a, trump, ledSuit) - cardStrength(b, trump, ledSuit));
    return canBeat[0];
  }

  // Survival mode + void in led suit: trump in with best trump to secure the trick
  if (survivalMode && followable.length === 0) {
    const myTrumpCards = playable.filter(c => effectiveSuit(c, trump) === trump);
    if (myTrumpCards.length > 0) {
      myTrumpCards.sort((a, b) => cardStrength(b, trump, trump) - cardStrength(a, trump, trump));
      return myTrumpCards[0];
    }
  }

  // Can't win: dump strategically
  if (handState && followable.length === 0) {
    const nonTrump = playable.filter((c) => effectiveSuit(c, trump) !== trump);
    if (nonTrump.length > 0) {
      const suitCounts = {};
      for (const c of hand) {
        const s = effectiveSuit(c, trump);
        if (s !== trump) suitCounts[s] = (suitCounts[s] || 0) + 1;
      }
      nonTrump.sort((a, b) => {
        const sA = effectiveSuit(a, trump);
        const sB = effectiveSuit(b, trump);
        const countA = suitCounts[sA] || 0;
        const countB = suitCounts[sB] || 0;
        if (countA !== countB) return countA - countB;
        return cardStrength(a, trump, null) - cardStrength(b, trump, null);
      });
      return nonTrump[0];
    }
  }

  playable.sort((a, b) => cardStrength(a, trump, ledSuit) - cardStrength(b, trump, ledSuit));

  if (followable.length === 0) {
    const nonTrump = playable.filter((c) => effectiveSuit(c, trump) !== trump);
    if (nonTrump.length > 0) {
      nonTrump.sort((a, b) => cardStrength(a, trump, null) - cardStrength(b, trump, null));
      return nonTrump[0];
    }
  }

  return playable[0];
}

// ─── AI COACH SUGGESTIONS ────────────────────────────────────────────────────
function generateTrumpSuggestionRound1(hand, turnedUp, playerIdx, dealerIdx) {
  const potentialTrump = turnedUp.suit;
  let trumpCount = 0;
  const trumpCards = [];

  for (const c of hand) {
    if (effectiveSuit(c, potentialTrump) === potentialTrump) {
      trumpCount++;
      trumpCards.push(cardKey(c));
    }
  }

  const hasRB = hand.some((c) => isRightBower(c, potentialTrump));
  const hasLB = hand.some((c) => isLeftBower(c, potentialTrump));
  const hasAce = hand.some((c) => c.rank === "A" && effectiveSuit(c, potentialTrump) === potentialTrump);
  const isDealer = playerIdx === dealerIdx;

  let action, reasoning;

  // Strong recommendations
  if (hasRB && hasLB) {
    action = isDealer ? "pick_up" : "order_up";
    reasoning = `You have both Jacks (Right & Left Bower)! This is a very strong trump hand.`;
  } else if (hasRB && trumpCount >= 3) {
    action = isDealer ? "pick_up" : "order_up";
    reasoning = `You have the Right Bower plus ${trumpCount} trump cards. Strong hand!`;
  } else if (hasRB && hasAce) {
    action = isDealer ? "pick_up" : "order_up";
    reasoning = `Right Bower + Ace of trump is a solid combination.`;
  } else if (trumpCount >= 4) {
    action = isDealer ? "pick_up" : "order_up";
    reasoning = `You have ${trumpCount} trump cards. Strength in numbers!`;
  } else if (hasRB && trumpCount >= 2) {
    action = isDealer ? "pick_up" : "order_up";
    reasoning = `Right Bower with ${trumpCount} trump gives you good control.`;
  } else if (hasLB && trumpCount >= 3) {
    action = isDealer ? "pick_up" : "order_up";
    reasoning = `Left Bower with ${trumpCount} trump is worth trying.`;
  } else if (isDealer && (hasRB || hasLB || trumpCount >= 2)) {
    action = "pick_up";
    reasoning = `As dealer, you get the turned up card. With ${trumpCount} trump${hasRB ? " and Right Bower" : hasLB ? " and Left Bower" : ""}, it's worth picking up.`;
  } else {
    action = "pass";
    if (trumpCount === 0) {
      reasoning = `No trump cards in hand. Passing is the safe choice.`;
    } else if (trumpCount === 1 && !hasRB && !hasLB) {
      reasoning = `Only ${trumpCount} low trump. Not enough to call confidently.`;
    } else {
      reasoning = `With ${trumpCount} trump and no Bowers, passing is safer.`;
    }
  }

  return { action, reasoning, trumpCards, trumpCount };
}

function generateTrumpSuggestionRound2(hand, turnedUpSuit, playerIdx, dealerIdx) {
  const availableSuits = SUITS.filter((s) => s !== turnedUpSuit);
  let bestSuit = null;
  let bestCount = 0;
  let bestHasRB = false;
  let bestHasLB = false;
  let reasoning = "";

  for (const suit of availableSuits) {
    let count = 0;
    let hasRB = false;
    let hasLB = false;

    for (const c of hand) {
      if (effectiveSuit(c, suit) === suit) {
        count++;
        if (isRightBower(c, suit)) hasRB = true;
        if (isLeftBower(c, suit)) hasLB = true;
      }
    }

    const score = count + (hasRB ? 2 : 0) + (hasLB ? 1.5 : 0);
    const bestScore = bestCount + (bestHasRB ? 2 : 0) + (bestHasLB ? 1.5 : 0);

    if (score > bestScore) {
      bestSuit = suit;
      bestCount = count;
      bestHasRB = hasRB;
      bestHasLB = hasLB;
    }
  }

  const isDealer = playerIdx === dealerIdx;

  if (bestHasRB && bestHasLB) {
    reasoning = `Both Jacks in ${bestSuit}! Call it confidently.`;
    return { action: bestSuit, reasoning };
  } else if (bestHasRB && bestCount >= 2) {
    reasoning = `Right Bower plus ${bestCount} ${bestSuit} is strong.`;
    return { action: bestSuit, reasoning };
  } else if (bestCount >= 3) {
    reasoning = `You have ${bestCount} ${bestSuit} cards. Worth calling.`;
    return { action: bestSuit, reasoning };
  } else if (isDealer && (bestCount >= 2 || bestHasRB || bestHasLB)) {
    reasoning = `As dealer, ${bestSuit} is your best option with ${bestCount} cards${bestHasRB ? " and Right Bower" : bestHasLB ? " and Left Bower" : ""}. Consider calling.`;
    return { action: bestSuit, reasoning };
  } else if (bestHasRB || (bestHasLB && bestCount >= 2)) {
    reasoning = `${bestHasRB ? "Right" : "Left"} Bower in ${bestSuit} makes it worth a try.`;
    return { action: bestSuit, reasoning };
  } else {
    reasoning = `No strong suit available. Best option is ${bestSuit} with ${bestCount} cards, but passing is safer.`;
    return { action: "pass", reasoning, fallbackSuit: bestSuit };
  }
}

function generatePlaySuggestion(hand, trick, trump) {
  if (!hand || hand.length === 0) return null;

  const ledSuit = trick.length > 0 ? effectiveSuit(trick[0].card, trump) : null;
  const followable = ledSuit ? hand.filter((c) => effectiveSuit(c, trump) === ledSuit) : hand;
  const playable = followable.length > 0 ? followable : hand;

  let suggestion, reasoning;

  // Leading the trick
  if (trick.length === 0) {
    const trumpCards = playable.filter((c) => effectiveSuit(c, trump) === trump);
    const offAces = playable.filter((c) => c.rank === "A" && effectiveSuit(c, trump) !== trump);
    const hasRB = trumpCards.some(c => isRightBower(c, trump));

    if (trumpCards.length >= 3) {
      trumpCards.sort((a, b) => cardStrength(b, trump, trump) - cardStrength(a, trump, trump));
      suggestion = trumpCards[0];
      reasoning = `Lead your highest trump to draw out opponents' trump. You have ${trumpCards.length} trump cards.`;
    } else if (offAces.length > 0 && trumpCards.length <= 1) {
      suggestion = offAces[0];
      reasoning = `Lead your off-suit Ace. It's likely to win the trick and preserves your trump.`;
    } else if (hasRB) {
      suggestion = trumpCards.find(c => isRightBower(c, trump));
      reasoning = `Lead the Right Bower (highest card) to guarantee this trick.`;
    } else if (trumpCards.length > 0) {
      trumpCards.sort((a, b) => cardStrength(a, trump, trump) - cardStrength(b, trump, trump));
      suggestion = trumpCards[Math.floor(trumpCards.length / 2)];
      reasoning = `Lead a mid-strength trump to draw out higher trump economically.`;
    } else {
      playable.sort((a, b) => cardStrength(b, trump, null) - cardStrength(a, trump, null));
      suggestion = playable[0];
      reasoning = `No trump available. Lead your highest card.`;
    }
  } else {
    // Following the trick
    let bestStrength = -1;
    for (const t of trick) {
      const s = cardStrength(t.card, trump, ledSuit);
      if (s > bestStrength) bestStrength = s;
    }

    const canBeat = playable.filter((c) => cardStrength(c, trump, ledSuit) > bestStrength);
    const mustFollow = followable.length > 0;

    if (canBeat.length > 0) {
      canBeat.sort((a, b) => cardStrength(a, trump, ledSuit) - cardStrength(b, trump, ledSuit));
      suggestion = canBeat[0];
      reasoning = `Play your lowest winning card to win economically.`;
    } else if (mustFollow) {
      playable.sort((a, b) => cardStrength(a, trump, ledSuit) - cardStrength(b, trump, ledSuit));
      suggestion = playable[0];
      reasoning = `Can't beat the current high card. Play your lowest ${ledSuit} to minimize loss.`;
    } else {
      // Sluffing
      const nonTrump = playable.filter((c) => effectiveSuit(c, trump) !== trump);
      if (nonTrump.length > 0) {
        nonTrump.sort((a, b) => cardStrength(a, trump, null) - cardStrength(b, trump, null));
        suggestion = nonTrump[0];
        reasoning = `Void in led suit. Throw your lowest off-suit card to save trump.`;
      } else {
        playable.sort((a, b) => cardStrength(a, trump, ledSuit) - cardStrength(b, trump, ledSuit));
        suggestion = playable[0];
        reasoning = `No off-suit cards. Sacrifice your lowest trump.`;
      }
    }
  }

  return { card: suggestion, cardKey: cardKey(suggestion), reasoning };
}

// ─── EVENT LOGGER ─────────────────────────────────────────────────────────────
function createLogger() {
  const log = [];
  let gameId = null;
  return {
    setGameId(id) {
      gameId = id;
    },
    emit(moveType, payload) {
      log.push({
        move_id: uuidv4(),
        game_id: gameId,
        move_type: moveType,
        timestamp: new Date().toISOString(),
        ...payload,
      });
    },
    getLog() {
      return log;
    },
    reset() {
      log.length = 0;
      gameId = null;
    },
  };
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function BuckEuchre() {
  const HUMAN = 0;
  const navigate = useNavigate();

  // Responsive sizing
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const isPhone = Math.min(windowWidth, windowHeight) < 500;
  const isMobile = windowWidth < 768 || isPhone;
  const isTablet = !isPhone && windowWidth >= 768 && windowWidth < 1024;
  const isLandscapePhone = isPhone && windowWidth > windowHeight;

  // Mobile table dimensions — all player/trump positions are derived from these
  const MT_W = 170;   // mobile felt table width (portrait: narrow)
  const MT_H = 350;   // mobile felt table height (portrait: tall)

  // Responsive card sizes
  const cardSize = useMemo(() => {
    if (isMobile) return { w: 54, h: 78, fontSize: 12, suitSize: 30 };
    if (isTablet) return { w: 56, h: 82, fontSize: 12, suitSize: 32 };
    return { w: 68, h: 100, fontSize: 13, suitSize: 38 }; // Desktop
  }, [isMobile, isTablet]);

  // Authentication - use context (auth is handled by router/LandingPage)
  const { user: currentUser, token, signout, updateUser, isClubMember } = useAuth();

  // Free play mode detection from URL
  const [searchParams] = useSearchParams();
  const isFreePlayUrl = searchParams.get('mode') === 'free';
  const isShowcase = searchParams.get('showcase') === 'true'; // AI-only spectator mode
  const isGuest = !currentUser;

  // Player count from URL (default 3)
  const playerCount = searchParams.get('players') === '4' ? 4 : 3;
  const ALL_PLAYERS = useMemo(() => playerCount === 4 ? [0, 1, 2, 3] : [0, 1, 2], [playerCount]);

  // Multiplayer mode detection
  const communityRoom = searchParams.get('community');
  const isMultiplayer = !!communityRoom;
  const isHost = searchParams.get('host') === 'true';
  const isLivePlay = searchParams.get('liveplay') === 'true';
  const isAutoStart = searchParams.get('autostart') === 'true';
  const isSpectator = searchParams.get('spectate') === 'true';
  const mySeat = isMultiplayer ? (isHost ? 0 : parseInt(searchParams.get('seat') || '0')) : 0;
  // Rotate display so the local player always appears at bottom (like single-player)
  const rotOffset = mySeat; // how many seats to rotate counter-clockwise
  const dSeat = (actual) => (actual - rotOffset + playerCount) % playerCount; // actual → display position
  // Display position aliases: 0=bottom, 1=right(3p)/left(4p), 2=top, 3=right(4p)
  const seatAtBottom = mySeat;
  const seatAtTop    = playerCount === 3 ? (mySeat + 2) % 3 : (mySeat + 2) % 4; // seat opposite
  const seatAtRight  = playerCount === 3 ? (mySeat + 1) % 3 : (mySeat + 3) % 4;
  const seatAtLeft   = playerCount === 4 ? (mySeat + 1) % 4 : -1; // 4p only
  const socketRef = useRef(null);
  // Seed multiplayerPlayers from URL params so seats are correct immediately
  const [multiplayerPlayers, setMultiplayerPlayers] = useState(() => {
    try {
      const seated = searchParams.get('seated');
      if (seated) return JSON.parse(seated);
    } catch { /* ignore parse errors */ }
    return [];
  });
  const [, setRemoteGameState] = useState(null);

  // Setup - skip auth phase since routing handles it
  const [setupPhase, setSetupPhase] = useState(isFreePlayUrl ? "wager" : "wager");
  const [wagerMode, setWagerMode] = useState(null);
  const [buyinDollars, setBuyinDollars] = useState(2);
  const [perPointDollars, setPerPointDollars] = useState(0.05);
  const [freePlayAggression, setFreePlayAggression] = useState(1.0);
  const [balanceError, setBalanceError] = useState("");
  const [adWatchMsg, setAdWatchMsg] = useState("");

  // Game
  const [gameId, setGameId] = useState(null);
  const gameIdRef = useRef(null);

  const [phase, setPhase] = useState("idle");

  const [scores, setScores] = useState([]);
  const scoresRef = useRef([]);
  useEffect(() => {
    scoresRef.current = scores;
  }, [scores]);

  const [scoreHistory, setScoreHistory] = useState([]);

  const [hands, setHands] = useState(playerCount === 4 ? [[], [], [], []] : [[], [], []]);
  const [dealer, setDealer] = useState(null);

  const [turnedUp, setTurnedUp] = useState(null);
  const [crib, setCrib] = useState([]); // 4 cards for 3p, 0 cards for 4p

  const [trump, setTrump] = useState(null);
  const [trumpCaller, setTrumpCaller] = useState(null);
  const [dealerPickedUp, setDealerPickedUp] = useState(false); // Track if dealer picked up turned-up card

  const [playingPlayers, setPlayingPlayers] = useState([]); // players active in hand
  const [playDecisions, setPlayDecisions] = useState({}); // { pid: true/false } only for non caller, non spade hands

  const [currentTrick, setCurrentTrick] = useState([]);
  const [tricksWon, setTricksWon] = useState(playerCount === 4 ? [0, 0, 0, 0] : [0, 0, 0]);
  const [allTricks, setAllTricks] = useState([]);
  const [trickPauseCards, setTrickPauseCards] = useState(null);
  const [handState, setHandState] = useState(null); // v2: intra-hand card tracking
  const [callerSeatPos, setCallerSeatPos] = useState(null); // seat position of trump caller

  const [whoseTurn, setWhoseTurn] = useState(null);

  const [round1Passes, setRound1Passes] = useState(0);
  const [round2Passes, setRound2Passes] = useState(0);

  const [handNumber, setHandNumber] = useState(0);
  const [handResult, setHandResult] = useState(null);

  const [aiTick, setAiTick] = useState(0);

  // Track if balance was updated for current game (to prevent duplicate updates)
  const balanceUpdatedRef = useRef(false);
  const gameSavedRef = useRef(false);
  const abandonPayloadRef = useRef(null);

  // Fetch server-side AI training params on mount
  useEffect(() => {
    import('./apiClient').then(({ getAITrainingParams }) => {
      getAITrainingParams().then(params => {
        if (params) {
          learningModule.setServerParams(params);
          console.log('AI training params loaded from server:', params.bestTrumpCallThreshold, params.aggressivenessMultiplier);
        }
      });
    });
  }, []);

  // AI player names and comments
  const [aiPlayerNames, setAiPlayerNames] = useState(["Player 2", "Player 3"]);
  const [aiComment, setAiComment] = useState(null); // { playerIdx, text, type: 'pass' | 'order' }

  // Fast-forward mode: speed up AI-only play when human is sitting out
  const [fastForward, setFastForward] = useState(false);
  // AI Speed Up: reduce AI delay for faster gameplay (especially mobile)
  const [aiSpeedUp, setAiSpeedUp] = useState(false);
  // Auto Deal: automatically deal next hand after a delay (host only in multiplayer)
  const [autoDeal, setAutoDeal] = useState(false);
  const [autoDealDelay, setAutoDealDelay] = useState(5); // seconds
  const [showMobileSettings, setShowMobileSettings] = useState(false);

  // AI takeover: when human leaves mid-game, AI finishes it
  const [humanAbandoned, setHumanAbandoned] = useState(false);
  const [rpEarned, setRpEarned] = useState(null);
  const [tipSent, setTipSent] = useState({});
  const [tipPicker, setTipPicker] = useState(null);

  // AI Coach suggestion state
  const [aiCoachEnabled, setAiCoachEnabled] = useState(false);
  const [botSuggestion, setBotSuggestion] = useState(null); // { action, reasoning } for round1
  const [round2Suggestion, setRound2Suggestion] = useState(null); // { action, reasoning } for round2
  const [playSuggestion, setPlaySuggestion] = useState(null); // { card, cardKey, reasoning }

  const [findDealerState, setFindDealerState] = useState(null);
  const findDealerDeckRef = useRef(null);
  const findDealerIdxRef = useRef(0);

  const [dealerWinBanner, setDealerWinBanner] = useState(null);

  const [dealAnim, setDealAnim] = useState(null); // { seq, idx, finalHands, turnedUp, crib, dealerIdx, handNum }
  const [handHistory, setHandHistory] = useState([]);
  const [showHandHistory, setShowHandHistory] = useState(false);
  const [showLastTrick, setShowLastTrick] = useState(false);
  const timeoutRef = useRef(null);
  const logRef = useRef(createLogger());

  const playerLabels = (() => {
    if (isMultiplayer && multiplayerPlayers.length > 0) {
      return Array.from({ length: playerCount }, (_, i) => {
        const player = multiplayerPlayers.find(p => p.seatIndex === i);
        if (player) return player.nickname;
        // AI seat: count non-human seats before this one to index into aiPlayerNames
        const aiIndex = Array.from({ length: i }, (_, j) => j)
          .filter(j => !multiplayerPlayers.some(p => p.seatIndex === j)).length;
        return aiPlayerNames[aiIndex] || `AI ${i + 1}`;
      });
    }
    return playerCount === 4
      ? [currentUser?.nickname || "You", aiPlayerNames[0], aiPlayerNames[1], aiPlayerNames[2] || "Player 4"]
      : [currentUser?.nickname || "You", aiPlayerNames[0], aiPlayerNames[1]];
  })();

  const nextPlayer = useCallback((p) => {
    if (playerCount === 4) {
      return (p + 1) % 4; // Normal rotation for 4 players
    } else {
      return (p + 2) % 3; // Skip one for 3 players
    }
  }, [playerCount]);

  const btnS = (bg) => ({
    background: bg,
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "7px 18px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
  });

  function exportLog() {
    const data = JSON.stringify(logRef.current.getLog(), null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "buck-euchre-" + (gameIdRef.current || "").slice(0, 8) + ".json";
    a.click();
    URL.revokeObjectURL(url);
  }

  function hardResetHandState() {
    const emptyHands = playerCount === 4 ? [[], [], [], []] : [[], [], []];
    const emptyTricks = playerCount === 4 ? [0, 0, 0, 0] : [0, 0, 0];

    setHands(emptyHands);
    setTurnedUp(null);
    setCrib([]);
    setTrump(null);
    setTrumpCaller(null);
    setDealerPickedUp(false); // Reset for new hand
    setPlayingPlayers([]);
    setPlayDecisions({});
    setCurrentTrick([]);
    setTricksWon(emptyTricks);
    setAllTricks([]);
    setTrickPauseCards(null);
    setWhoseTurn(null);
    setRound1Passes(0);
    setRound2Passes(0);
    setHandResult(null);
    setHandState(null);
    setCallerSeatPos(null);
  }

  // ─── GAME STATE SERIALIZATION (for save/resume) ─────────────────────────
  function serializeGameState() {
    return {
      gameId: gameIdRef.current || gameId,
      playerCount,
      wagerMode,
      buyinDollars,
      perPointDollars,
      aiPlayerNames,
      phase,
      scores: [...scoresRef.current],
      scoreHistory: [...scoreHistory],
      handNumber,
      hands: hands.map(h => h.map(c => ({ rank: c.rank, suit: c.suit }))),
      dealer,
      turnedUp: turnedUp ? { rank: turnedUp.rank, suit: turnedUp.suit } : null,
      crib: crib.map(c => ({ rank: c.rank, suit: c.suit })),
      trump,
      trumpCaller,
      dealerPickedUp,
      playingPlayers: [...playingPlayers],
      playDecisions: { ...playDecisions },
      currentTrick: currentTrick.map(t => ({ player: t.player, card: { rank: t.card.rank, suit: t.card.suit } })),
      tricksWon: [...tricksWon],
      allTricks: allTricks.map(t => ({
        cards: t.cards.map(c => ({ player: c.player, card: { rank: c.card.rank, suit: c.card.suit } })),
        winner: t.winner
      })),
      whoseTurn,
      round1Passes,
      round2Passes,
      freePlayAggression,
      handHistory: [...handHistory],
      savedAt: new Date().toISOString()
    };
  }

  function restoreGameState(snapshot) {
    gameIdRef.current = snapshot.gameId;
    setGameId(snapshot.gameId);
    setWagerMode(snapshot.wagerMode);
    setBuyinDollars(snapshot.buyinDollars);
    setPerPointDollars(snapshot.perPointDollars);
    setAiPlayerNames(snapshot.aiPlayerNames);
    setScores(snapshot.scores);
    setScoreHistory(snapshot.scoreHistory);
    setHandNumber(snapshot.handNumber);
    setHands(snapshot.hands);
    setDealer(snapshot.dealer);
    setTurnedUp(snapshot.turnedUp);
    setCrib(snapshot.crib);
    setTrump(snapshot.trump);
    setTrumpCaller(snapshot.trumpCaller);
    setDealerPickedUp(snapshot.dealerPickedUp);
    setPlayingPlayers(snapshot.playingPlayers);
    setPlayDecisions(snapshot.playDecisions);
    setCurrentTrick(snapshot.currentTrick);
    setTricksWon(snapshot.tricksWon);
    setAllTricks(snapshot.allTricks);
    setWhoseTurn(snapshot.whoseTurn);
    setRound1Passes(snapshot.round1Passes);
    setRound2Passes(snapshot.round2Passes);
    if (snapshot.freePlayAggression !== undefined) {
      setFreePlayAggression(snapshot.freePlayAggression);
    }
    if (snapshot.handHistory) setHandHistory(snapshot.handHistory);
    setSetupPhase("playing");
    setPhase(snapshot.phase);
    setAiTick(x => x + 1);

    logRef.current.reset();
    logRef.current.setGameId(snapshot.gameId);
    logRef.current.emit("game_resumed", { resumedAt: new Date().toISOString(), handNumber: snapshot.handNumber });
  }

  // ─── MULTIPLAYER: SOCKET CONNECTION & EVENTS ────────────────────────────
  // Determine which seats are remote humans (not AI, not local)
  const remoteHumanSeats = useMemo(() => {
    if (!isMultiplayer) return [];
    return multiplayerPlayers
      .filter(p => p.seatIndex !== mySeat)
      .map(p => p.seatIndex);
  }, [isMultiplayer, multiplayerPlayers, mySeat]);

  // Ref so AI timeout callbacks always see the latest remoteHumanSeats without stale closure
  const remoteHumanSeatsRef = useRef(remoteHumanSeats);
  useEffect(() => { remoteHumanSeatsRef.current = remoteHumanSeats; }, [remoteHumanSeats]);

  // Keep abandon payload ref current so unmount cleanup has fresh data
  // Skip for multiplayer — other players keep playing, server handles disconnect grace period
  useEffect(() => {
    const isActiveGame = setupPhase === 'playing' && !['idle', 'gameOver'].includes(phase) && !humanAbandoned && !gameSavedRef.current && !isMultiplayer;
    if (!isActiveGame) {
      abandonPayloadRef.current = null;
      return;
    }
    const players = ALL_PLAYERS.map(i => ({
      playerIndex: i,
      isAI: i !== HUMAN && !remoteHumanSeats.includes(i),
      aiName: (i !== HUMAN && !remoteHumanSeats.includes(i)) ? aiPlayerNames[i - 1] : null,
      userId: i === HUMAN ? currentUser?.userId : null
    }));
    abandonPayloadRef.current = JSON.stringify({
      gameId: gameIdRef.current, scores: scoresRef.current, dealer, playerCount, handNumber,
      wagerMode, players, aiNames: aiPlayerNames,
      token, buyinDollars, perPointDollars
    });
  }, [setupPhase, phase, humanAbandoned, gameId, dealer, playerCount, handNumber, wagerMode, aiPlayerNames, token, buyinDollars, perPointDollars, currentUser, ALL_PLAYERS, remoteHumanSeats, isMultiplayer]);

  // On browser close/reload: send beacon to finish game server-side instantly
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!abandonPayloadRef.current) return;
      e.preventDefault();
      e.returnValue = '';
      navigator.sendBeacon(`${API_BASE_URL}/games/finish-abandoned`, new Blob([abandonPayloadRef.current], { type: 'application/json' }));
      abandonPayloadRef.current = null;
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // On component unmount (React Router navigation / browser back): finish game server-side
  useEffect(() => {
    return () => {
      if (abandonPayloadRef.current) {
        navigator.sendBeacon(`${API_BASE_URL}/games/finish-abandoned`, new Blob([abandonPayloadRef.current], { type: 'application/json' }));
        abandonPayloadRef.current = null;
      }
    };
  }, []);

  // Connect socket for multiplayer
  useEffect(() => {
    if (!isMultiplayer || !token) return;
    const sock = connectSocket(token);
    socketRef.current = sock;

    // Get players list from room info
    sock.on('player-joined', (data) => setMultiplayerPlayers(data.players));
    sock.on('player-left', (data) => setMultiplayerPlayers(data.players));

    // Disconnect grace period events
    sock.on('player-disconnected', (data) => {
      console.log(`⏳ ${data.nickname} disconnected (seat ${data.seatIndex}) — ${data.gracePeriodSeconds}s to reconnect`);
      setMultiplayerPlayers(data.players);
    });
    sock.on('player-reconnected', (data) => {
      console.log(`🔄 ${data.reconnectedNickname} reconnected (seat ${data.seatIndex})`);
      setMultiplayerPlayers(data.players);
    });
    sock.on('player-ai-takeover', (data) => {
      console.log(`🤖 AI taking over seat ${data.seatIndex} (was ${data.nickname})`);
      // Remove from multiplayerPlayers so remoteHumanSeats excludes this seat → AI takes over
      setMultiplayerPlayers(data.players);
    });
    sock.on('host-disconnected', () => {
      console.log('🏠 Host disconnected — game ending');
      alert('The host has left the game. Returning to lobby.');
      navigate('/community-play');
    });

    // Live Play: host registers socket with the live play room
    if (isLivePlay && isHost && communityRoom) {
      sock.emit('liveplay:register', { roomCode: communityRoom });
      // Listen for incoming players — moved here to guarantee it's on the active socket
      sock.on('liveplay:seat_taken', ({ seatIndex, nickname, socketId, userId }) => {
        setMultiplayerPlayers(prev => [
          ...prev.filter(p => p.seatIndex !== seatIndex),
          { userId, nickname, seatIndex, socketId },
        ]);
        console.log(`[LivePlay] ${nickname} took seat ${seatIndex}`);
      });
    }
    // Live Play: non-host registers to receive seat assignment
    if (isLivePlay && !isHost && communityRoom) {
      const mySeatIdx = parseInt(searchParams.get('seat') || '0');
      sock.emit('liveplay:join', { roomCode: communityRoom, seatIndex: mySeatIdx });
    }
    // Live Play: room closed by host disconnect
    sock.on('liveplay:room_closed', () => {
      alert('The Live Play table has closed. Returning home.');
      navigate('/home');
    });

    return () => {
      if (isLivePlay && communityRoom) sock.emit('liveplay:leave', { roomCode: communityRoom, seatIndex: mySeat });
      leaveRoom();
    };
  }, [isMultiplayer, token]);

  // Live Play host: signal between-hands + accept seat swaps
  useEffect(() => {
    if (!isLivePlay || !isHost || !communityRoom) return;
    const sock = socketRef.current;
    if (!sock) return;
    if (phase === 'handOver') {
      sock.emit('liveplay:between_hands', { roomCode: communityRoom });
    } else if (['dealing', 'round1', 'playing'].includes(phase)) {
      sock.emit('liveplay:playing', { roomCode: communityRoom });
    }
  }, [phase, isLivePlay, isHost, communityRoom]);


  // HOST: Listen for remote player actions
  useEffect(() => {
    if (!isMultiplayer || !isHost) return;
    const sock = socketRef.current;
    if (!sock) return;

    const onAction = (action) => {
      const { type, fromSeat } = action;
      if (fromSeat === undefined) return;

      if (type === 'playCard' && phase === 'playing' && whoseTurn === fromSeat) {
        const card = hands[fromSeat]?.find(c => c.rank === action.card.rank && c.suit === action.card.suit);
        if (card) handlePlayCard(card, fromSeat);
      } else if (type === 'pass' && phase === 'round1' && whoseTurn === fromSeat) {
        handleTrumpRound1('pass', fromSeat);
      } else if (type === 'orderUp' && phase === 'round1' && whoseTurn === fromSeat) {
        handleTrumpRound1('orderUp', fromSeat);
      } else if (type === 'pass' && phase === 'round2' && whoseTurn === fromSeat) {
        handleTrumpRound2(null, fromSeat);
      } else if (type === 'callTrump' && phase === 'round2' && whoseTurn === fromSeat) {
        handleTrumpRound2(action.suit, fromSeat);
      } else if (type === 'discard' && phase === 'discard' && whoseTurn === fromSeat) {
        const card = hands[fromSeat]?.find(c => c.rank === action.card.rank && c.suit === action.card.suit);
        if (card) handleDiscard(card, fromSeat);
      } else if (type === 'decidePlay' && phase === 'decide_play' && whoseTurn === fromSeat) {
        handleDecidePlay(action.decision, fromSeat);
      }
    };

    sock.on('player-action', onAction);
    return () => sock.off('player-action', onAction);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiplayer, isHost, phase, whoseTurn, hands]);

  // HOST: Broadcast game state after every relevant change
  useEffect(() => {
    if (!isMultiplayer || !isHost) return;
    if (setupPhase !== 'playing') return;

    const state = serializeGameState();
    state.multiplayerPlayers = multiplayerPlayers;
    sendGameState(state);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, whoseTurn, scores, hands, currentTrick, tricksWon, turnedUp, trump, trumpCaller, dealer, playingPlayers, playDecisions, allTricks, handResult, dealerPickedUp, handNumber, scoreHistory, handHistory, multiplayerPlayers]);

  // NON-HOST / SPECTATOR: Listen for game state broadcasts and update local state
  const [spectatorCount, setSpectatorCount] = useState(0);
  useEffect(() => {
    if (!isMultiplayer || isHost) return;
    const sock = socketRef.current;
    if (!sock) return;

    const applyState = (state) => {
      setRemoteGameState(state);
      if (state.multiplayerPlayers) setMultiplayerPlayers(state.multiplayerPlayers);
      // Update local state from host's broadcast
      setPhase(state.phase);
      setScores(state.scores);
      setScoreHistory(state.scoreHistory);
      setHandNumber(state.handNumber);
      setHands(state.hands);
      setDealer(state.dealer);
      setTurnedUp(state.turnedUp);
      setCrib(state.crib || []);
      setTrump(state.trump);
      setTrumpCaller(state.trumpCaller);
      setDealerPickedUp(state.dealerPickedUp);
      setPlayingPlayers(state.playingPlayers || []);
      setPlayDecisions(state.playDecisions || {});
      setCurrentTrick(state.currentTrick || []);
      setTricksWon(state.tricksWon || []);
      setAllTricks(state.allTricks || []);
      setWhoseTurn(state.whoseTurn);
      setRound1Passes(state.round1Passes);
      setRound2Passes(state.round2Passes);
      setAiPlayerNames(state.aiPlayerNames || []);
      if (state.wagerMode) setWagerMode(state.wagerMode);
      if (state.handHistory) setHandHistory(state.handHistory);
      setSetupPhase('playing');
    };

    const eventName = isSpectator ? 'spectator-state' : 'game-state';
    sock.on(eventName, applyState);

    const onSpectatorCount = ({ count }) => setSpectatorCount(count);
    sock.on('spectator-count', onSpectatorCount);

    return () => {
      sock.off(eventName, applyState);
      sock.off('spectator-count', onSpectatorCount);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMultiplayer, isHost, isSpectator]);

  function startGame() {
    // Select random AI names for this game (only for actual AI seats in multiplayer)
    const shuffledNames = [...AI_NAMES].sort(() => Math.random() - 0.5);
    const aiSeatCount = isMultiplayer ? playerCount - multiplayerPlayers.length : playerCount - 1;
    setAiPlayerNames(shuffledNames.slice(0, aiSeatCount));

    const gid = uuidv4();
    gameIdRef.current = gid;
    setGameId(gid);

    logRef.current.reset();
    logRef.current.setGameId(gid);

    const initScores = playerCount === 4
      ? [GAME_START_SCORE, GAME_START_SCORE, GAME_START_SCORE, GAME_START_SCORE]
      : [GAME_START_SCORE, GAME_START_SCORE, GAME_START_SCORE];
    setScores(initScores);
    setScoreHistory([]);
    setHandHistory([]);
    setHandNumber(0);

    hardResetHandState();

    // Prepare find dealer
    setDealer(null);
    setDealerWinBanner(null);
    findDealerDeckRef.current = shuffle(buildDeck(playerCount));
    findDealerIdxRef.current = 0;

    setPhase("findDealer");
    setFindDealerState({
      dealt: playerCount === 4 ? [[], [], [], []] : [[], [], []],
      currentPlayer: 0,
      winner: null,
    });
  }

  // ─── LIVE PLAY / AUTO-START: skip wager screen, start immediately in free play ──
  const livePlayStarted = useRef(false);
  useEffect(() => {
    if (!isAutoStart || !isHost || livePlayStarted.current) return;
    livePlayStarted.current = true;
    setWagerMode('none');
    setSetupPhase('playing');
    setTimeout(() => startGame(), 60);
  }, [isAutoStart, isHost]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── SHOWCASE MODE: auto-start all-AI game ──────────────────────────────
  const showcaseStarted = useRef(false);
  useEffect(() => {
    if (!isShowcase || showcaseStarted.current) return;
    showcaseStarted.current = true;
    // Use AI names from URL if provided, otherwise random
    const urlNames = searchParams.get('aiNames');
    if (urlNames) {
      try {
        const names = JSON.parse(urlNames);
        setAiPlayerNames(names.slice(0, playerCount - 1));
      } catch { /* ignore */ }
    }
    setWagerMode("none");
    setSetupPhase("playing");
    setHumanAbandoned(true); // AI controls all seats from the start
    setTimeout(() => startGame(), 100);
  }, [isShowcase]);

  // ─── FIND DEALER ANIMATION ────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== "findDealer") return;
    if (!findDealerState) return;

    if (findDealerState.winner !== null) return;

    const t = setTimeout(() => {
      const deck = findDealerDeckRef.current || shuffle(buildDeck());
      const idx = findDealerIdxRef.current;

      if (idx >= deck.length) {
        // restart if somehow out
        findDealerDeckRef.current = shuffle(buildDeck(playerCount));
        findDealerIdxRef.current = 0;
        setFindDealerState({ dealt: ALL_PLAYERS.map(() => []), currentPlayer: 0, winner: null });
        return;
      }

      const card = deck[idx];
      findDealerIdxRef.current = idx + 1;

      const p = findDealerState.currentPlayer;
      const newDealt = findDealerState.dealt.map((h, i) => (i === p ? [...h, card] : h));

      if (isBlackJack(card)) {
        setDealerWinBanner(`${playerLabels[p]} deals first!`);
        setFindDealerState({
          dealt: newDealt,
          currentPlayer: p,
          winner: p,
        });

        logRef.current.emit("game_start", {
          player_count: playerCount,
          start_score: GAME_START_SCORE,
          wager_mode: wagerMode,
          buyin: buyinDollars,
          per_point: perPointDollars,
          initial_dealer: p,
        });

        setTimeout(() => {
          setDealer(p);
          setDealerWinBanner(null);
          setFindDealerState(null);
          dealHand(gidSafe(), p, scoresRef.current, 1);
        }, FIND_DEAL_WIN_PAUSE_MS);
      } else {
        setFindDealerState({
          dealt: newDealt,
          currentPlayer: nextPlayer(p),
          winner: null,
        });
      }
    }, FIND_DEAL_CARD_MS);

    return () => clearTimeout(t);
  }, [phase, findDealerState, nextPlayer, wagerMode, buyinDollars, perPointDollars]);

  function gidSafe() {
    return gameIdRef.current || gameId || uuidv4();
  }

  // ─── DEAL HAND (GENERATE + ANIMATE) ────────────────────────────────────────
  function buildDealSequence(dealerIdx, finalHands) {
    // Build deal order: start left of dealer, go around
    const order = [];
    let p = nextPlayer(dealerIdx);
    for (let i = 0; i < playerCount; i++) {
      order.push(p);
      p = nextPlayer(p);
    }

    const seq = [];
    // Traditional euchre dealing: 3 cards to each player, then 2 cards to each player
    // Pass 1: 3 cards to 1st player, 3 to 2nd, 3 to 3rd (and 4th in 4P)
    for (const pl of order) {
      for (let c = 0; c < 3; c++) {
        seq.push({ player: pl, card: finalHands[pl][c] });
      }
    }
    // Pass 2: 2 cards to 1st player, 2 to 2nd, 2 to 3rd (and 4th in 4P)
    for (const pl of order) {
      for (let c = 3; c < 5; c++) {
        seq.push({ player: pl, card: finalHands[pl][c] });
      }
    }

    return seq;
  }

  function dealHand(gidSafe, dealerIdx, currentScores, handNum) {
    // Crib must disappear right before the deal starts
    setCrib([]);
    setTurnedUp(null);

    const deck = shuffle(buildDeck(playerCount));
    const finalHands = ALL_PLAYERS.map(() => []);

    // Build deal order: start left of dealer, go around
    const order = [];
    let p = nextPlayer(dealerIdx);
    for (let i = 0; i < playerCount; i++) {
      order.push(p);
      p = nextPlayer(p);
    }

    let idx = 0;

    // Pass 1: 3 cards to each player
    for (const pl of order) {
      finalHands[pl] = deck.slice(idx, idx + 3);
      idx += 3;
    }
    // Pass 2: 2 cards to each player
    for (const pl of order) {
      finalHands[pl].push(...deck.slice(idx, idx + 2));
      idx += 2;
    }

    const kittyTop = deck[idx];
    // 3-player: 4 crib cards; 4-player: 3 burned cards (not shown)
    const cribRest = playerCount === 3 ? deck.slice(idx + 1) : [];

    const handsLog = {};
    ALL_PLAYERS.forEach(i => { handsLog[`p${i}`] = finalHands[i].map(cardKey); });

    logRef.current.emit("hand_deal", {
      hand_number: handNum,
      dealer_id: dealerIdx,
      turned_up_card: cardKey(kittyTop),
      hands_dealt: handsLog,
      crib: cribRest.map(cardKey),
      scores_before: [...currentScores],
    });

    hardResetHandState();
    setHandNumber(handNum);

    const seq = buildDealSequence(dealerIdx, finalHands);

    setHands(ALL_PLAYERS.map(() => []));
    setPhase("dealing");
    setDealAnim({
      seq,
      idx: 0,
      finalHands,
      turnedUpCard: kittyTop,
      cribRest,
      dealerIdx,
      handNum,
    });
  }

  // Deal animation tick
  useEffect(() => {
    if (phase !== "dealing") return;
    if (!dealAnim) return;

    if (dealAnim.idx >= dealAnim.seq.length) {
      // finished dealing cards, reveal turned up and crib after a short beat
      const t = setTimeout(() => {
        setTurnedUp(dealAnim.turnedUpCard);
        setCrib(dealAnim.cribRest);

        // Delay so humans can see the turned up before option 1 appears
        setTimeout(() => {
          const kitty = dealAnim.turnedUpCard;

          // Auto trump spades: spades is trump, dealer is maker, everyone plays
          if (kitty.suit === "♠") {
            setTrump("♠");
            setTrumpCaller(dealAnim.dealerIdx);
            setCallerSeatPos(playerCount - 1); // dealer is always last seat
            setPlayingPlayers([...ALL_PLAYERS]);
            setPlayDecisions({});
            setRound1Passes(0);
            setRound2Passes(0);

            logRef.current.emit("auto_trump_spade", { dealer_id: dealAnim.dealerIdx });

            // Delay before dealer picks up the card (let everyone see it's spades)
            setTimeout(() => {
              // Dealer picks up turned up card and discards, but do not go to decide_play
              setHands((prev) => {
                const nh = prev.map((h) => [...h]);
                nh[dealAnim.dealerIdx] = [...nh[dealAnim.dealerIdx], kitty];
                return nh;
              });

              setDealerPickedUp(true); // Mark that dealer picked up the spades card
              setPhase("discard");
              setWhoseTurn(dealAnim.dealerIdx);
              setAiTick((x) => x + 1);
            }, 1200); // 1.2 second delay to see the spade
          } else {
            setPhase("round1");
            setWhoseTurn(nextPlayer(dealAnim.dealerIdx));
            setAiTick((x) => x + 1);
          }

          setDealAnim(null);
        }, TURNED_UP_REVEAL_DELAY_MS);
      }, 300);

      return () => clearTimeout(t);
    }

    const t = setTimeout(() => {
      const step = dealAnim.seq[dealAnim.idx];
      if (!step || !step.card) {
        setDealAnim((d) => ({ ...d, idx: d.idx + 1 }));
        return;
      }

      setHands((prev) => {
        const nh = prev.map((h) => [...h]);
        nh[step.player] = [...nh[step.player], step.card];
        return nh;
      });

      setDealAnim((d) => ({ ...d, idx: d.idx + 1 }));
    }, DEAL_ANIM_MS);

    return () => clearTimeout(t);
  }, [phase, dealAnim, nextPlayer]);

  // ─── AI TURN LOOP ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    if (whoseTurn === null) return;
    // When human abandoned, AI takes over their seat too
    if (whoseTurn === HUMAN && !humanAbandoned) return;
    // In multiplayer, skip remote human players (they send actions via socket)
    if (isMultiplayer && isHost && remoteHumanSeats.includes(whoseTurn)) return;
    // Non-host never runs AI logic
    if (isMultiplayer && !isHost) return;
    if (!["round1", "round2", "discard", "playing", "decide_play"].includes(phase)) return;
    if (trickPauseCards) return; // Prevent AI from acting during trick pause

    const aggOverride = wagerMode === "none" ? 2.0 : undefined;
    timeoutRef.current = setTimeout(() => {
      // Re-check with ref to catch players who joined after the timeout was scheduled
      if (isMultiplayer && isHost && remoteHumanSeatsRef.current.includes(whoseTurn)) return;
      if (phase === "round1") {
        handleTrumpRound1(aiTrumpDecisionRound1(hands[whoseTurn], turnedUp, whoseTurn, dealer, aggOverride, playerCount, scoresRef.current), whoseTurn);
      } else if (phase === "round2") {
        handleTrumpRound2(aiTrumpDecisionRound2(hands[whoseTurn], turnedUp?.suit, whoseTurn, dealer, aggOverride, playerCount, scoresRef.current), whoseTurn);
      } else if (phase === "discard") {
        const c = aiDiscard(hands[whoseTurn], trump);
        handleDiscard(c, whoseTurn);
      } else if (phase === "playing") {
        const c = aiPlayCard(hands[whoseTurn], currentTrick, trump, trumpCaller, whoseTurn, handState);
        if (c) handlePlayCard(c, whoseTurn);
      } else if (phase === "decide_play") {
        // If spades, this phase should never happen, but guard anyway
        if (trump === "♠" && turnedUp?.suit === "♠") return;
        // Build alreadyDecided from current playDecisions
        const playCount = Object.values(playDecisions).filter(v => v === true).length;
        const sitCount = Object.values(playDecisions).filter(v => v === false).length;
        handleDecidePlay(aiDecideToPlay(hands[whoseTurn], trump, aggOverride, scoresRef.current, whoseTurn, trumpCaller, callerSeatPos, { playing: playCount, sitting: sitCount }, playerCount), whoseTurn);
      }
    // Round1: keep a human-visible pause even in speed mode so the turned-up card can be seen
    }, humanSitting && fastForward ? 150 + Math.random() * 80 : (phase === "round1" && aiSpeedUp) ? 900 + Math.random() * 100 : aiSpeedUp ? 600 + Math.random() * 200 : 1400 + Math.random() * 400);

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [phase, whoseTurn, aiTick, hands, turnedUp, dealer, trump, currentTrick, trickPauseCards, fastForward, aiSpeedUp, humanAbandoned, multiplayerPlayers]);

  // ─── AI COACH: GENERATE SUGGESTIONS FOR HUMAN ───────────────────────────────
  useEffect(() => {
    // Clear suggestions when not human's turn
    if (whoseTurn !== HUMAN) {
      setBotSuggestion(null);
      setRound2Suggestion(null);
      setPlaySuggestion(null);
      return;
    }

    // Generate trump calling suggestion for round1
    if (phase === "round1" && hands[HUMAN]?.length > 0 && turnedUp) {
      const suggestion = generateTrumpSuggestionRound1(hands[HUMAN], turnedUp, HUMAN, dealer);
      setBotSuggestion(suggestion);
    } else {
      setBotSuggestion(null);
    }

    // Generate trump calling suggestion for round2
    if (phase === "round2" && hands[HUMAN]?.length > 0 && turnedUp) {
      const suggestion = generateTrumpSuggestionRound2(hands[HUMAN], turnedUp.suit, HUMAN, dealer);
      setRound2Suggestion(suggestion);
    } else {
      setRound2Suggestion(null);
    }

    // Generate play card suggestion
    if (phase === "playing" && hands[HUMAN]?.length > 0 && trump) {
      const suggestion = generatePlaySuggestion(hands[HUMAN], currentTrick, trump);
      setPlaySuggestion(suggestion);
    } else {
      setPlaySuggestion(null);
    }
  }, [phase, whoseTurn, hands, turnedUp, dealer, trump, currentTrick]);

  // ─── GAME OVER: SAVE GAME, UPDATE BALANCES, UPDATE ANALYTICS ──────────────
  useEffect(() => {
    if (phase === "gameOver") {
      const goWinnerIdx = scores.indexOf(Math.min(...scores));
      hapticNotification(goWinnerIdx === myPlayerSeat ? 'success' : 'error');

      const gameLog = logRef.current.getLog();
      if (gameLog && gameLog.length > 0 && !gameSavedRef.current) {
        gameSavedRef.current = true;
        abandonPayloadRef.current = null; // game completed normally, don't fire abandon on unmount
        try {
          const insights = learningModule.analyzeGame(gameLog);
          console.log("AI Learning Updated:", {
            gamesAnalyzed: insights.gamesAnalyzed,
            trumpCallThreshold: insights.patterns.bestTrumpCallThreshold,
            playThreshold: insights.patterns.bestPlayThreshold,
            aggression: insights.patterns.aggressivenessMultiplier.toFixed(2),
          });

          // Build players array — include userId for all human seats (local + remote live play)
          const players = ALL_PLAYERS.map(i => ({
            playerIndex: i,
            isAI: i !== HUMAN && !remoteHumanSeats.includes(i),
            aiName: (i !== HUMAN && !remoteHumanSeats.includes(i)) ? aiPlayerNames[i - 1] : null,
            userId: i === HUMAN
              ? currentUser?.userId
              : (remoteHumanSeats.includes(i) ? (multiplayerPlayers.find(p => p.seatIndex === i)?.userId || null) : null),
          }));

          // Save game to SQLite database — THEN update balances after save confirms
          fetch(`${API_BASE_URL}/games/save`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {})
            },
            body: JSON.stringify({
              gameId, playerCount, wagerMode, gameLog,
              aiLearningSnapshot: insights,
              buyinDollars, perPointDollars, players,
              abandoned: humanAbandoned,
              gameType: 'buck'
            })
          })
          .then(res => res.json())
          .then(result => {
            if (result.success) {
              console.log("✅ Game saved to database!");

              // Update all rank fields from server response
              if (result.rankPoints !== undefined && result.rankPoints !== null) {
                updateUser({
                  rankPoints: result.rankPoints,
                  rankName: result.rankName,
                  activeRP: result.activeRP,
                  activeToNextSuit: result.activeToNextSuit,
                  nextSuitLevel: result.nextSuitLevel,
                  suitLevel: result.suitLevel,
                  suitSymbol: result.suitSymbol,
                  suitLevelProgress: result.suitLevelProgress,
                  cardRankId: result.cardRankId,
                  displayRank: result.displayRank,
                  lifetimeRP: result.lifetimeRP,
                });
              }
              if (result.rankPointsEarned !== undefined && result.rankPointsEarned !== null) {
                setRpEarned(result.rankPointsEarned);
              }

              // Update human balance (chained after game save to avoid race condition)
              if (wagerMode && wagerMode !== "none" && token && !balanceUpdatedRef.current) {
                balanceUpdatedRef.current = true;
                const winnerIdx = scores.indexOf(Math.min(...scores));
                const payouts = scores.map((s, i) => {
                  if (i === winnerIdx) return null;
                  return buyinDollars + Math.max(s, 0) * perPointDollars;
                });
                const totalWon = payouts.reduce((sum, p) => sum + (p || 0), 0);
                const humanPayout = winnerIdx === HUMAN ? totalWon : -(payouts[HUMAN] || 0);

                if (humanPayout !== 0) {
                  fetch(`${API_BASE_URL}/users/balance/update`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ amountChange: humanPayout, wagerMode, gameId })
                  })
                  .then(res => res.json())
                  .then(data => {
                    if (data.success) {
                      const userUpdate = {
                        playMoneyBalance: data.playMoneyBalance,
                        realMoneyBalance: data.realMoneyBalance
                      };
                      // Also pick up rank data from balance endpoint response
                      if (data.rankPoints !== undefined && data.rankPoints !== null) {
                        userUpdate.rankPoints = data.rankPoints;
                        userUpdate.rankName = data.rankName;
                      }
                      updateUser(userUpdate);
                      console.log(`💰 Bucks updated: ${humanPayout >= 0 ? '+' : ''}${humanPayout.toFixed(0)} Bucks`);
                    }
                  })
                  .catch(err => console.error('Balance update failed:', err));
                }
              }

              // Update AI balances
              if (wagerMode && wagerMode !== "none" && aiPlayerNames.length > 0) {
                const winnerIdx = scores.indexOf(Math.min(...scores));
                const payouts = scores.map((s, i) => {
                  if (i === winnerIdx) return null;
                  return buyinDollars + Math.max(s, 0) * perPointDollars;
                });
                const totalWon = payouts.reduce((sum, p) => sum + (p || 0), 0);

                const aiUpdates = aiPlayerNames.map((name, aiIdx) => {
                  const playerIdx = aiIdx + 1;
                  let delta;
                  if (playerIdx === winnerIdx) {
                    delta = totalWon;
                  } else {
                    delta = -(payouts[playerIdx] || 0);
                  }
                  return {
                    aiName: name,
                    playDelta: wagerMode === "play" ? delta : 0,
                    realDelta: wagerMode === "real" ? delta : 0
                  };
                });

                import('./apiClient').then(({ updateAIBalances }) => {
                  updateAIBalances(aiUpdates)
                    .then(r => { if (r.success) console.log("AI balances updated"); });
                });
              }
            } else {
              console.error("❌ Database save FAILED:", result.error || result.message || JSON.stringify(result));
              gameSavedRef.current = false; // allow retry
            }
          })
          .catch(err => {
            console.error("❌ Could not save to database (server may be offline):", err.message);
            gameSavedRef.current = false; // allow retry
          })
          .finally(() => {
            // FALLBACK: Always refresh user data from server after game ends.
            // This ensures rank points, balances, and badges are always up to date,
            // even if the game save response didn't include rank data.
            if (token) {
              fetch(`${API_BASE_URL}/auth/me`, {
                headers: { 'Authorization': `Bearer ${token}` }
              })
              .then(r => r.json())
              .then(me => {
                const src = me?.user || me;
                if (src && src.userId) {
                  console.log(`🔄 Post-game user refresh: activeRP=${src.activeRP} → ${src.activeToNextSuit} to ${src.nextSuitLevel}`);
                  updateUser({
                    rankPoints: src.rankPoints,
                    rankName: src.rankName,
                    playMoneyBalance: src.playMoneyBalance,
                    activeRP: src.activeRP,
                    activeToNextSuit: src.activeToNextSuit,
                    nextSuitLevel: src.nextSuitLevel,
                    suitLevel: src.suitLevel,
                    suitSymbol: src.suitSymbol,
                    suitLevelProgress: src.suitLevelProgress,
                    cardRankId: src.cardRankId,
                    displayRank: src.displayRank,
                    lifetimeRP: src.lifetimeRP,
                  });
                }
              })
              .catch(() => {}); // silent — best-effort refresh
            }
          });

        } catch (e) {
          console.error("Failed to analyze game:", e);
        }
      }

      // Guest free play: increment game counter
      if (isGuest && wagerMode === "none" && !balanceUpdatedRef.current) {
        balanceUpdatedRef.current = true;
        const prev = parseInt(localStorage.getItem("buck_euchre_guest_games") || "0", 10);
        localStorage.setItem("buck_euchre_guest_games", String(prev + 1));
      }
    }

    // Reset the flag when starting a new game
    if (phase === "dealing") {
      balanceUpdatedRef.current = false;
      gameSavedRef.current = false;
    }
  }, [phase, gameId, playerCount, wagerMode, scores, buyinDollars, perPointDollars, token, updateUser, isGuest, aiPlayerNames, currentUser, ALL_PLAYERS, remoteHumanSeats, humanAbandoned]);

  // ─── AUTO-SAVE GAME SNAPSHOT ON HAND OVER ──────────────────────────────────
  const snapshotSavedRef = useRef(false);
  useEffect(() => {
    if (phase === "handOver" && token && !isGuest && !snapshotSavedRef.current) {
      snapshotSavedRef.current = true;
      const state = serializeGameState();
      import('./apiClient').then(({ saveGameSnapshot }) => {
        saveGameSnapshot(token, state.gameId, JSON.stringify(state))
          .then(result => {
            if (result.success) console.log("Game snapshot saved");
            else console.warn("Snapshot save failed:", result.error);
          });
      });
    }
    if (phase === "dealing") {
      snapshotSavedRef.current = false;
    }
  }, [phase, token, isGuest]);

  // ─── AUTO-ADVANCE WHEN AI HAS TAKEN OVER ──────────────────────────────────
  useEffect(() => {
    if (!humanAbandoned) return;
    if (phase === "handOver") {
      const t = setTimeout(() => nextHand(), 500);
      return () => clearTimeout(t);
    }
    if (phase === "gameOver" && !isShowcase) {
      // Auto-navigate home after brief delay (not in showcase — user wants to see results)
      const t = setTimeout(() => navigate("/home"), 3000);
      return () => clearTimeout(t);
    }
  }, [phase, humanAbandoned]);

  // ─── AUTO-DEAL: automatically advance to next hand after delay ───────────
  useEffect(() => {
    if (!autoDeal) return;
    if (phase !== "handOver") return;
    if (humanAbandoned) return; // already handled above
    // In multiplayer, only host should auto-deal
    if (isMultiplayer && !isHost) return;
    const t = setTimeout(() => nextHand(), autoDealDelay * 1000);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, autoDeal, autoDealDelay, humanAbandoned, isMultiplayer, isHost]);

  // ─── DELETE SNAPSHOT ON GAME OVER ────────────────────────────────────────
  useEffect(() => {
    if (phase !== "gameOver") return;
    const gid = gameIdRef.current || gameId;
    if (!gid || !token || isGuest) return;

    fetch(`${API_BASE_URL}/games/snapshot`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ gameId: gid })
    })
    .then(res => res.json())
    .then(result => { if (result.success) console.log("Game snapshot deleted"); })
    .catch(() => {});
  }, [phase]);

  // ─── RESUME GAME FROM URL ────────────────────────────────────────────────
  const resumeHandled = useRef(false);
  useEffect(() => {
    const resumeGameId = searchParams.get('resume');
    if (!resumeGameId || !token || resumeHandled.current) return;
    resumeHandled.current = true;

    import('./apiClient').then(({ resumeGame }) => {
      resumeGame(token, resumeGameId).then(data => {
        if (data && data.gameState) {
          const snapshot = typeof data.gameState === 'string' ? JSON.parse(data.gameState) : data.gameState;
          restoreGameState(snapshot);
          console.log("Game resumed from snapshot");
        } else {
          console.warn("No snapshot found for game", resumeGameId);
        }
      });
    });
  }, [token, searchParams]);

  // ─── TRUMP ROUND 1 ─────────────────────────────────────────────────────────
  function handleTrumpRound1(decision, pid) {
    logRef.current.emit("trump_round1", {
      player_id: pid,
      decision,
      turned_up_card: cardKey(turnedUp),
    });

    // Show AI comment for non-human players
    if (pid !== HUMAN) {
      const sayings = (decision === "pass") ? AI_PASS_SAYINGS : AI_ORDER_SAYINGS;
      let randomSaying = sayings[Math.floor(Math.random() * sayings.length)];
      // Replace {trump} placeholder with actual suit
      if (turnedUp?.suit) {
        randomSaying = randomSaying.replace(/\{trump\}/gi, turnedUp.suit);
      }
      // setAiComment({ playerIdx: pid, text: randomSaying, type: decision === "pass" ? "pass" : "order" });
      // setTimeout(() => setAiComment(null), humanAbandoned ? 200 : 2500);
    }

    if (decision === "pick_up" || decision === "order_up") {
      // Dealer always picks up the turned up card
      setHands((prev) => {
        const nh = prev.map((h) => [...h]);
        nh[dealer] = [...nh[dealer], turnedUp];
        return nh;
      });

      setTrump(turnedUp.suit);
      setTrumpCaller(pid);
      setCallerSeatPos(getSeatPositionFn(pid, dealer, playerCount));
      setDealerPickedUp(true); // Mark that dealer picked up the card

      // Delay so humans can see the pickup moment before discard UI
      setPhase("pickup_pause");
      setWhoseTurn(null);

      setTimeout(() => {
        setPhase("discard");
        setWhoseTurn(dealer);
        setAiTick((x) => x + 1);
      }, 900);

      return;
    }

    // pass
    const np = round1Passes + 1;
    setRound1Passes(np);

    if (np >= playerCount) {
      setPhase("round2");
      setRound2Passes(0);
      setWhoseTurn(nextPlayer(dealer));
      setAiTick((x) => x + 1);
    } else {
      setWhoseTurn(nextPlayer(pid));
      setAiTick((x) => x + 1);
    }
  }

  // ─── TRUMP ROUND 2 ─────────────────────────────────────────────────────────
  function handleTrumpRound2(decision, pid) {
    logRef.current.emit("trump_round2", {
      player_id: pid,
      decision: decision === "pass" ? "pass" : "call_" + decision,
      turned_up_suit: turnedUp?.suit,
    });

    // Show AI comment for non-human players
    if (pid !== HUMAN) {
      const sayings = (decision === "pass") ? AI_PASS_SAYINGS : AI_NAMING_SAYINGS;
      let randomSaying = sayings[Math.floor(Math.random() * sayings.length)];
      // Replace {trump} placeholder with actual suit (in round 2, decision IS the suit)
      if (decision !== "pass") {
        randomSaying = randomSaying.replace(/\{trump\}/gi, decision);
      }
      // setAiComment({ playerIdx: pid, text: randomSaying, type: decision === "pass" ? "pass" : "name" });
      // setTimeout(() => setAiComment(null), humanAbandoned ? 200 : 2500);
    }

    if (decision !== "pass") {
      setTrump(decision);
      setTrumpCaller(pid);
      setCallerSeatPos(getSeatPositionFn(pid, dealer, playerCount));

      // After trump is decided, non callers choose Play or Sit Out (except spades)
      setPhase("decide_play");
      setPlayDecisions({});
      setPlayingPlayers([pid]); // caller always plays
      // First non-caller to decide is the player left of trump caller, clockwise
      let first = nextPlayer(pid);
      setWhoseTurn(first);
      setAiTick((x) => x + 1);
      return;
    }

    const np = round2Passes + 1;
    setRound2Passes(np);

    // "Stick the dealer" — if all players (including dealer) pass, dealer loses their turn
    if (np >= playerCount) {
      logRef.current.emit("stick_dealer", { dealer, hand: handNumber });

      // Add stick dealer entry to scoreHistory so every hand appears on scorecard
      setScoreHistory((sh) => [
        ...sh,
        { hand: handNumber, deltas: new Array(playerCount).fill(0), reason: "Stick the dealer" },
      ]);

      setHandResult({ outcome: "stick_dealer", scoreDelta: new Array(playerCount).fill(0) });
      setPhase("handOver");
      return;
    }

    setWhoseTurn(nextPlayer(pid));
    setAiTick((x) => x + 1);
  }

  // ─── DECIDE PLAY OR SIT OUT ────────────────────────────────────────────────
  function handleDecidePlay(willPlay, pid) {
    // Spades is auto, everyone plays, so do not allow this phase
    if (turnedUp?.suit === "♠" && trump === "♠") return;

    // Only non callers decide
    if (pid === trumpCaller) return;

    logRef.current.emit("decide_play", {
      player_id: pid,
      will_play: willPlay,
      trump,
      trump_caller: trumpCaller,
    });

    // Show AI comment for play/sit decision
    if (pid !== HUMAN && !willPlay) {
      // const randomSaying = AI_SIT_SAYINGS[Math.floor(Math.random() * AI_SIT_SAYINGS.length)];
      // setAiComment({ playerIdx: pid, text: randomSaying, type: "sit" });
      // setTimeout(() => setAiComment(null), humanAbandoned ? 200 : 2500);
    } else if (pid !== HUMAN && willPlay) {
      // const randomSaying = AI_PLAY_SAYINGS[Math.floor(Math.random() * AI_PLAY_SAYINGS.length)];
      // setAiComment({ playerIdx: pid, text: randomSaying, type: "play" });
      // setTimeout(() => setAiComment(null), humanAbandoned ? 200 : 2500);
    }

    setPlayDecisions((prev) => ({ ...prev, [pid]: willPlay }));

    setPlayingPlayers((prev) => {
      const set = new Set(prev);
      if (willPlay) set.add(pid);
      return Array.from(set);
    });

    const nonCallers = ALL_PLAYERS.filter((p) => p !== trumpCaller);

    const decisions = { ...playDecisions, [pid]: willPlay };
    const allDecided = nonCallers.every((p) => decisions[p] !== undefined);

    if (!allDecided) {
      // Walk clockwise from current player to find next undecided non-caller
      let nextDecider = nextPlayer(pid);
      while (nextDecider === trumpCaller || decisions[nextDecider] !== undefined) {
        nextDecider = nextPlayer(nextDecider);
      }
      setWhoseTurn(nextDecider);
      setAiTick((x) => x + 1);
      return;
    }

    // Everyone decided
    const finalPlaying = [trumpCaller, ...nonCallers.filter((p) => decisions[p] === true)];

    setPlayingPlayers(finalPlaying);

    if (finalPlaying.length === 1) {
      // Trump caller alone gets -5
      const delta = new Array(playerCount).fill(0);
      delta[trumpCaller] = -5;
      const newScores = scoresRef.current.map((s, i) => s + delta[i]);

      setScores(newScores);
      setScoreHistory((sh) => [
        ...sh,
        { hand: handNumber, deltas: delta, reason: "Trump caller alone" },
      ]);

      logRef.current.emit("trump_caller_alone", {
        trump_caller: trumpCaller,
        score_delta: delta,
        scores_after: newScores,
      });

      setHandResult({ outcome: "alone", scoreDelta: delta });

      if (newScores.some((s) => s <= 0)) {
        const winner = newScores.indexOf(Math.min(...newScores));
        logRef.current.emit("game_over", { winner, final_scores: newScores });
        setPhase("gameOver");
      } else {
        setPhase("handOver");
      }
      return;
    }

    // Start play. Player left of dealer leads (skip non-playing players).
    setPhase("playing");
    setHandState(createHandState(playerCount, trump, finalPlaying));
    let firstPlayer = nextPlayer(dealer);
    let safety = 0;
    while (!finalPlaying.includes(firstPlayer) && safety < 6) {
      firstPlayer = nextPlayer(firstPlayer);
      safety++;
    }
    setWhoseTurn(firstPlayer);
    setAiTick((x) => x + 1);
  }

  // ─── DISCARD ───────────────────────────────────────────────────────────────
  function handleDiscard(card, pid) {
    if (!card) return;

    logRef.current.emit("discard", { player_id: pid, discarded_card: cardKey(card), trump });

    setHands((prev) => {
      const nh = prev.map((h) => [...h]);
      nh[pid] = nh[pid].filter((c) => cardKey(c) !== cardKey(card));
      return nh;
    });

    // If spades auto trump, everyone plays and we go straight to playing
    // Player left of dealer leads.
    if (turnedUp?.suit === "♠" && trump === "♠") {
      setPhase("playing");
      setHandState(createHandState(playerCount, trump, [...ALL_PLAYERS]));
      setWhoseTurn(nextPlayer(dealer));
      setAiTick((x) => x + 1);
      return;
    }

    // Otherwise, decide play — first non-caller is left of trump caller
    setPhase("decide_play");
    setPlayDecisions({});
    setPlayingPlayers([trumpCaller]);
    const first = nextPlayer(trumpCaller);
    setWhoseTurn(first);
    setAiTick((x) => x + 1);
  }

  // ─── PLAY CARD ─────────────────────────────────────────────────────────────
  function handlePlayCard(card, pid) {
    if (!card) return;
    if (!playingPlayers.includes(pid)) return;

    const myHand = hands[pid] || [];
    if (!myHand.some((c) => cardKey(c) === cardKey(card))) return;

    const ledSuit = currentTrick.length > 0 ? effectiveSuit(currentTrick[0].card, trump) : null;

    logRef.current.emit("play_card", {
      player_id: pid,
      card_played: cardKey(card),
      trump,
      led_suit: ledSuit,
      trick_number: allTricks.length + 1,
      hand_at_play: myHand.map(cardKey),
    });

    const newTrick = [...currentTrick, { player: pid, card }];

    setHands((prev) => {
      const nh = prev.map((h) => [...h]);
      nh[pid] = nh[pid].filter((c) => cardKey(c) !== cardKey(card));
      return nh;
    });

    const trickPlayers = newTrick.filter((t) => playingPlayers.includes(t.player));

    if (trickPlayers.length === playingPlayers.length) {
      const tLedSuit = effectiveSuit(trickPlayers[0].card, trump);
      let bestIdx = 0;
      let bestStr = -1;

      trickPlayers.forEach((t, i) => {
        const s = cardStrength(t.card, trump, tLedSuit);
        if (s > bestStr) {
          bestStr = s;
          bestIdx = i;
        }
      });

      const trickWinner = trickPlayers[bestIdx].player;
      const newTW = [...tricksWon];
      newTW[trickWinner]++;

      const newAll = [...allTricks, { cards: newTrick, winner: trickWinner }];

      logRef.current.emit("trick_complete", {
        trick_number: newAll.length,
        cards: newTrick.map((t) => ({ player_id: t.player, card: cardKey(t.card) })),
        winner: trickWinner,
        trump,
      });

      setTrickPauseCards(newTrick);
      setCurrentTrick([]);

      if (trickWinner === myPlayerSeat) hapticFeedback('medium');

      // Update HandState with trick results
      setHandState(prev => prev ? updateHandState(prev, newTrick, trump, trickWinner) : prev);

      setTimeout(() => {
        setTrickPauseCards(null);
        setTricksWon(newTW);
        setAllTricks(newAll);

        if (newAll.length === 5) {
          resolveHand(newTW, newAll);
        } else {
          setWhoseTurn(trickWinner);
          setAiTick((x) => x + 1);
        }
      }, humanSitting && fastForward ? 300 : aiSpeedUp ? (isMobile ? 1200 : 700) : (isMobile ? 2200 : 1700));

      return;
    }

    // Continue trick to next active player
    setCurrentTrick(newTrick);

    let next = nextPlayer(pid);
    let safety = 0;
    while (!playingPlayers.includes(next) && safety < 6) {
      next = nextPlayer(next);
      safety++;
    }
    setWhoseTurn(next);
    setAiTick((x) => x + 1);
  }

  // ─── HAND RESOLUTION / SCORING ─────────────────────────────────────────────
  function resolveHand(tw, completedTricks) {
    const delta = new Array(playerCount).fill(0);
    let reason = "";

    // Spades auto trump rules:
    // dealer is trump caller, must get 3, each other player must get 1
    if (turnedUp?.suit === "♠" && trump === "♠") {
      const dealerTricks = tw[dealer] || 0;
      if (dealerTricks >= 3) {
        delta[dealer] = -dealerTricks;
        reason = `Spades: dealer got ${dealerTricks} tricks`;
      } else {
        delta[dealer] = 5;
        reason = "Spades: dealer set (+5)";
      }

      for (const p of ALL_PLAYERS) {
        if (p === dealer) continue;
        const t = tw[p] || 0;
        if (t >= 1) delta[p] = -t;
        else delta[p] = 5;
      }

      const newScores = scoresRef.current.map((s, i) => s + delta[i]);
      setScores(newScores);
      setScoreHistory((sh) => [...sh, { hand: handNumber, deltas: delta, reason }]);

      logRef.current.emit("hand_complete", {
        trump,
        trump_caller: dealer,
        tricks_won: tw,
        outcome: "spades_auto",
        score_delta: delta,
        scores_after: newScores,
        playing_players: [...ALL_PLAYERS],
      });

      setHandHistory(prev => [...prev, {
        handNumber, dealer, trump, trumpCaller: dealer,
        tricks: (completedTricks || allTricks).map(t => ({ cards: t.cards.map(c => ({ player: c.player, card: { rank: c.card.rank, suit: c.card.suit } })), winner: t.winner })),
        scoreDelta: delta, playingPlayers: [...ALL_PLAYERS], outcome: "spades_auto"
      }]);
      setHandResult({ outcome: "spades_auto", scoreDelta: delta, tricksWon: tw });

      if (newScores.some((s) => s <= 0)) {
        const winner = newScores.indexOf(Math.min(...newScores));
        logRef.current.emit("game_over", { winner, final_scores: newScores });
        setPhase("gameOver");
      } else {
        setPhase("handOver");
      }
      return;
    }

    // Normal hand rules:
    // caller must get 3 or set +5
    // players who chose to play must get 1 or set +5
    // players who sat out no change
    const callerTricks = tw[trumpCaller] || 0;
    if (callerTricks >= 3) {
      delta[trumpCaller] = -callerTricks;
      reason = `Trump caller got ${callerTricks} tricks`;
    } else {
      delta[trumpCaller] = 5;
      reason = "Trump caller set (+5)";
    }

    for (const p of ALL_PLAYERS) {
      if (p === trumpCaller) continue;

      // sat out
      if (!playingPlayers.includes(p)) {
        delta[p] = 0;
        continue;
      }

      const t = tw[p] || 0;
      if (t >= 1) delta[p] = -t;
      else delta[p] = 5;
    }

    const newScores = scoresRef.current.map((s, i) => s + delta[i]);
    setScores(newScores);
    setScoreHistory((sh) => [...sh, { hand: handNumber, deltas: delta, reason }]);

    logRef.current.emit("hand_complete", {
      trump,
      trump_caller: trumpCaller,
      tricks_won: tw,
      outcome: "normal",
      score_delta: delta,
      scores_after: newScores,
      playing_players: playingPlayers,
    });

    setHandHistory(prev => [...prev, {
      handNumber, dealer, trump, trumpCaller,
      tricks: (completedTricks || allTricks).map(t => ({ cards: t.cards.map(c => ({ player: c.player, card: { rank: c.card.rank, suit: c.card.suit } })), winner: t.winner })),
      scoreDelta: delta, playingPlayers: [...playingPlayers], outcome: "normal"
    }]);
    setHandResult({ outcome: "normal", scoreDelta: delta, tricksWon: tw });

    if (newScores.some((s) => s <= 0)) {
      const winner = newScores.indexOf(Math.min(...newScores));
      logRef.current.emit("game_over", { winner, final_scores: newScores });
      setPhase("gameOver");
    } else {
      setPhase("handOver");
    }
  }

  function nextHand() {
    // "Stick the dealer" — dealer loses turn, next player deals. No score change, same hand number.
    const isStickDealer = handResult?.outcome === "stick_dealer";
    const nd = nextPlayer(dealer); // always rotate to next dealer
    setDealer(nd);
    dealHand(gidSafe(), nd, scoresRef.current, isStickDealer ? handNumber : handNumber + 1);
  }

  // ─── HUMAN ACTIONS ─────────────────────────────────────────────────────────
  // In multiplayer non-host mode, send actions via socket to host
  const myPlayerSeat = isSpectator ? -1 : (isMultiplayer && !isHost) ? mySeat : HUMAN;

  // Is the human currently sitting out this hand (or AI has taken over)?
  const humanSitting = humanAbandoned || (playingPlayers.length > 0 && !playingPlayers.includes(myPlayerSeat));

  function onHumanTrumpRound1(decision) {
    if (whoseTurn !== myPlayerSeat || phase !== "round1") return;
    if (decision !== 'pass') hapticFeedback('medium');
    if (isMultiplayer && !isHost) {
      import('./socketClient').then(({ sendPlayerAction }) => sendPlayerAction({ type: decision === 'pass' ? 'pass' : 'orderUp', fromSeat: mySeat }));
      return;
    }
    handleTrumpRound1(decision, HUMAN);
  }

  function onHumanTrumpRound2(suitOrPass) {
    if (whoseTurn !== myPlayerSeat || phase !== "round2") return;
    if (suitOrPass) hapticFeedback('medium');
    if (isMultiplayer && !isHost) {
      import('./socketClient').then(({ sendPlayerAction }) => sendPlayerAction(suitOrPass ? { type: 'callTrump', suit: suitOrPass, fromSeat: mySeat } : { type: 'pass', fromSeat: mySeat }));
      return;
    }
    handleTrumpRound2(suitOrPass, HUMAN);
  }

  function onHumanDecidePlay(willPlay) {
    if (whoseTurn !== myPlayerSeat || phase !== "decide_play") return;
    if (isMultiplayer && !isHost) {
      import('./socketClient').then(({ sendPlayerAction }) => sendPlayerAction({ type: 'decidePlay', decision: willPlay, fromSeat: mySeat }));
      return;
    }
    handleDecidePlay(willPlay, HUMAN);
  }

  function onHumanDiscard(card) {
    if (whoseTurn !== myPlayerSeat || phase !== "discard") return;
    if (isMultiplayer && !isHost) {
      import('./socketClient').then(({ sendPlayerAction }) => sendPlayerAction({ type: 'discard', card: { rank: card.rank, suit: card.suit }, fromSeat: mySeat }));
      return;
    }
    handleDiscard(card, HUMAN);
  }

  function onHumanPlayCard(card) {
    if (whoseTurn !== myPlayerSeat || phase !== "playing") return;
    if (trickPauseCards) return;
    if (!card) return;

    // follow suit enforcement
    const ledSuit = currentTrick.length > 0 ? effectiveSuit(currentTrick[0].card, trump) : null;
    if (ledSuit) {
      const canFollow = (hands[myPlayerSeat] || []).some((c) => effectiveSuit(c, trump) === ledSuit);
      if (canFollow && effectiveSuit(card, trump) !== ledSuit) return;
    }

    hapticFeedback('light');

    if (isMultiplayer && !isHost) {
      import('./socketClient').then(({ sendPlayerAction }) => sendPlayerAction({ type: 'playCard', card: { rank: card.rank, suit: card.suit }, fromSeat: mySeat }));
      return;
    }
    handlePlayCard(card, HUMAN);
  }

  // ─── RENDER HELPERS ─────────────────────────────────────────────────────────
  function renderCard(card, onClick, dimmed, small) {
    if (!card) return null;

    const color = SUIT_COLORS[card.suit];
    // Responsive card sizes - small cards are 70% of regular
    const w = small ? Math.round(cardSize.w * 0.7) : cardSize.w;
    const h = small ? Math.round(cardSize.h * 0.7) : cardSize.h;
    const rankFontSize = small ? Math.round(cardSize.fontSize * 0.8) : cardSize.fontSize;
    const suitFontSize = small ? Math.round(cardSize.suitSize * 0.8) : cardSize.suitSize;

    return (
      <div
        key={cardKey(card)}
        onClick={onClick}
        style={{
          width: w,
          height: h,
          borderRadius: isMobile ? 5 : 7,
          background: "#fff",
          border: "2px solid " + (dimmed ? "#444" : "#bbb"),
          boxShadow: dimmed ? "none" : "0 2px 8px rgba(0,0,0,0.35)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          cursor: onClick ? "pointer" : "default",
          opacity: dimmed ? 0.45 : 1,
          userSelect: "none",
          transition: "transform 0.15s, box-shadow 0.15s",
          position: "relative",
          flexShrink: 0,
          overflow: "hidden"
        }}
        onMouseEnter={(e) => {
          if (onClick && !isMobile) {
            e.currentTarget.style.transform = "translateY(-8px)";
            e.currentTarget.style.boxShadow = "0 8px 18px rgba(0,0,0,0.45)";
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = dimmed ? "none" : "0 2px 8px rgba(0,0,0,0.35)";
        }}
      >
        {/* Card front watermark - ADJUST opacity and size */}
        <img
          src="/BuckEuchreLogo.png"
          alt=""
          style={{
            position: "absolute",
            width: w * 1.9,      // ADJUST: logo size relative to card
            height: "auto",
            opacity: 0.10,       // ADJUST: 0.05=very faint, 0.15=visible
            pointerEvents: "none",
            zIndex: 0
          }}
        />
        <div style={{ position: "absolute", top: 2, left: 4, fontSize: rankFontSize, fontWeight: 700, color, lineHeight: 1, zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0 }}>
          <span>{card.rank}</span>
          <span style={{ fontSize: Math.round(rankFontSize * 0.85) }}>{card.suit}</span>
        </div>
        <div style={{ fontSize: suitFontSize, color, fontWeight: 700, zIndex: 1, WebkitTextStroke: SUIT_STROKE[card.suit] }}>{card.suit}</div>
        <div
          style={{
            position: "absolute",
            bottom: 2,
            right: 4,
            fontSize: rankFontSize,
            fontWeight: 700,
            color,
            transform: "rotate(180deg)",
            lineHeight: 1,
            zIndex: 1,
            display: "flex", flexDirection: "column", alignItems: "center", gap: 0
          }}
        >
          <span>{card.rank}</span>
          <span style={{ fontSize: Math.round(rankFontSize * 0.85) }}>{card.suit}</span>
        </div>
      </div>
    );
  }

  function renderCardBack(small) {
    // Responsive sizes to match renderCard
    const w = small ? Math.round(cardSize.w * 0.7) : cardSize.w;
    const h = small ? Math.round(cardSize.h * 0.7) : cardSize.h;
    return (
      <div
        style={{
          width: w,
          height: h,
          borderRadius: isMobile ? 5 : 7,
          background: "linear-gradient(135deg, #6a8dd1 0%, #6e98e0 100%)",
          border: "2px solid #0f3460",
          boxShadow: "0 2px 6px rgba(0,0,0,0.35)",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          position: "relative"
        }}
      >
        {/* Card back logo - ADJUST opacity and size */}
        <img
          src="/BuckEuchreLogo.png"
          alt=""
          style={{
            width: w * 2.0,      // ADJUST: logo size relative to card
            height: "auto",
            opacity: .7,        // ADJUST: 0.3=subtle, 0.6=visible, 0.9=prominent
            pointerEvents: "none"
          }}
        />
      </div>
    );
  }

  function renderCribTrumpContainer() {
    // Don't show before game starts or if no dealer set
    if (dealer === null) return null;
    if (phase === "idle") return null;

    // Responsive container dimensions
    const CONTAINER_WIDTH = isMobile ? 52 : 82;
    const CONTAINER_HEIGHT = isMobile ? 72 : 112;
    const CRIB_AREA_HEIGHT = isMobile ? 56 : 88;
    const TRUMP_LABEL_HEIGHT = isMobile ? 16 : 24;

    // Position container in the corner nearest the dealer (counterclockwise from dealer's perspective)
    // dSeat(dealer): 0=bottom, 1=left(4p)/right(3p), 2=top, 3=right(4p)
    const dealerDisplay = dSeat(dealer);
    const offset = isMobile ? 8 : 10;
    let containerPosition = {};
    let containerRotate = 0;
    if (dealerDisplay === 0) {
      // bottom dealer → bottom-left corner, no rotation
      containerPosition = { bottom: offset, left: offset };
      containerRotate = 0;
    } else if (dealerDisplay === 1 && playerCount === 3) {
      // 3p right dealer → bottom-right corner, rotate -90deg (label faces right)
      containerPosition = { bottom: offset, right: offset };
      containerRotate = -90;
    } else if (dealerDisplay === 1 && playerCount === 4) {
      // 4p left dealer → top-left corner, rotate 90deg (label faces left)
      containerPosition = { top: offset, left: offset };
      containerRotate = 90;
    } else if (dealerDisplay === 2) {
      // top dealer → top-right corner, rotate 180deg (label faces up)
      containerPosition = { top: offset, right: offset };
      containerRotate = 180;
    } else if (dealerDisplay === 3) {
      // 4p right dealer → bottom-right corner, rotate -90deg (label faces right)
      containerPosition = { bottom: offset, right: offset };
      containerRotate = -90;
    }

    // Crib card backs always visible in 3-player mode (represents the dealt-aside cards)
    const showCribCards = playerCount === 3;

    // Determine if turned up card should be shown
    let showTurnedUp = false;
    if (turnedUp) {
      if (phase === "round1") {
        showTurnedUp = true;
      } else if (phase === "round2") {
        showTurnedUp = false;
      } else if (["discard", "decide_play", "pickup_pause", "playing"].includes(phase)) {
        if (dealerPickedUp) {
          // Check if card has been played
          const turnedUpKey = cardKey(turnedUp);
          let cardHasBeenPlayed = false;
          for (const trick of allTricks) {
            if (trick.cards.some((t) => cardKey(t.card) === turnedUpKey)) {
              cardHasBeenPlayed = true;
              break;
            }
          }
          showTurnedUp = !cardHasBeenPlayed;
        }
      }
    }

    return (
      <div style={{
        position: "absolute",
        ...containerPosition,
        width: CONTAINER_WIDTH,
        height: CONTAINER_HEIGHT,
        background: "#f5f5f5",
        borderRadius: 8,
        border: trump ? `2px solid ${SUIT_COLORS[trump]}` : "2px solid rgba(139, 92, 46, 0.6)",
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        zIndex: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        padding: 0,
        transform: `rotate(${containerRotate}deg)`,
        transformOrigin: "center",
      }}>
        {/* Upper area: crib cards or turned up card */}
        <div style={{
          height: CRIB_AREA_HEIGHT,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          position: "relative"
        }}>
          {/* Crib cards */}
          {showCribCards && (
            <div style={{ position: "relative", width: isMobile ? 38 : 54, height: isMobile ? 52 : 76 }}>
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  style={{
                    position: "absolute",
                    left: i * (isMobile ? 1 : 2),
                    top: i * (isMobile ? 1 : 2),
                    transform: "rotate(" + (i * 2) + "deg)",
                  }}
                >
                  {renderCardBack(true)}
                </div>
              ))}
            </div>
          )}

          {/* Turned up card */}
          {showTurnedUp && (
            <div style={{ position: "absolute", zIndex: 5 }}>
              {renderCard(turnedUp, null, false, true)}
            </div>
          )}
        </div>

        {/* Bottom area: trump suit indicator */}
        <div style={{
          height: TRUMP_LABEL_HEIGHT,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderTop: "1px solid rgba(0,0,0,0.15)",
          background: "#e8e8e8",
          borderRadius: isMobile ? "0 0 4px 4px" : "0 0 6px 6px"
        }}>
          {trump ? (
            <span style={{ color: SUIT_COLORS[trump], fontSize: isMobile ? 14 : 22, fontWeight: 700 }}>{trump}</span>
          ) : (
            <span style={{ color: "#9ca3af", fontSize: isMobile ? 7 : 10 }}>TRUMP</span>
          )}
        </div>
      </div>
    );
  }

  function renderNameplate(pIdx, rotateDeg = 0) {
    const isDealer = dealer === pIdx;
    const isTrumpCaller = trumpCaller === pIdx;
    const isSittingOut = trump && !playingPlayers.includes(pIdx) && !(turnedUp?.suit === "♠" && trump === "♠");
    const trickCount = tricksWon[pIdx] || 0;
    // Show green border when this player decided to play (and is not the trump caller, who always plays)
    const decidedToPlay = !isTrumpCaller && playDecisions[pIdx] === true && trump;
    const borderColor = decidedToPlay ? "#22c55e" : "#334155";
    const borderWidth = decidedToPlay ? (isMobile ? 2 : 3) : (isMobile ? 1 : 2);

    return (
      <div
        style={{
          transform: rotateDeg ? `rotate(${rotateDeg}deg)` : "none",
          transformOrigin: "center",
        }}
      >
        <div
          style={{
            background: "#fff",
            borderRadius: isMobile ? 5 : 8,
            padding: isMobile ? "3px 6px" : "6px 12px",
            border: `${borderWidth}px solid ${borderColor}`,
            display: "inline-flex",
            alignItems: "center",
            gap: isMobile ? 4 : 8,
            opacity: isSittingOut ? 0.4 : 1,
          }}
        >
          <span style={{ fontSize: isMobile ? 10 : 14, fontWeight: 700, color: "#1e293b" }}>{playerLabels[pIdx]}</span>
          <span style={{
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              width: isMobile ? 18 : 24, height: isMobile ? 18 : 24, borderRadius: "50%",
              background: "#facc15", color: "#000", fontWeight: 900,
              fontSize: isMobile ? 11 : 15, lineHeight: 1,
              boxShadow: isDealer ? "0 1px 3px rgba(0,0,0,0.3)" : "none",
              visibility: isDealer ? "visible" : "hidden"
            }}>D</span>
          {isTrumpCaller && trump && <span style={{ fontSize: isMobile ? 12 : 18, color: SUIT_COLORS[trump], fontWeight: 700 }}>{trump}</span>}
          <div style={{ display: "flex", gap: isMobile ? 1 : 3 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                style={{
                  width: isMobile ? 8 : 13,
                  height: isMobile ? 8 : 13,
                  borderRadius: isMobile ? 2 : 3,
                  background: i < trickCount ? "#4ade80" : "#cbd5e1",
                  border: "1px solid #94a3b8",
                }}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  function renderPlayerHand(pIdx, clickMode, rotateDeg = 0) {
    const h = hands[pIdx];
    // In multiplayer, the local player's seat is mySeat (not necessarily 0)
    const isLocalPlayer = isMultiplayer ? (pIdx === mySeat) : (pIdx === HUMAN);
    const isMyTurn = whoseTurn === pIdx;

    let onClick = null;
    if (isMyTurn && isLocalPlayer) {
      if (clickMode === "discard") onClick = (c) => onHumanDiscard(c);
      else if (clickMode === "play") onClick = (c) => onHumanPlayCard(c);
    }

    // Responsive wrapper that adjusts based on screen size
    const wrap = (children) => {
      const isRotated = rotateDeg === 90 || rotateDeg === -90;
      const wrapWidth = isMobile ? (isRotated ? 85 : 300) : (isRotated ? 110 : 380);
      const wrapHeight = isMobile ? (isRotated ? 260 : 85) : (isRotated ? 320 : 110);
      return (
        <div style={{
          transform: rotateDeg ? `rotate(${rotateDeg}deg)` : "none",
          transformOrigin: "center",
          width: wrapWidth,
          height: wrapHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>{children}</div>
      );
    };

    if (!h || h.length === 0) return wrap(<div />); // Return empty fixed-size container

    const cardGap = isMobile ? 1 : 3;

    if (!isLocalPlayer) {
      return wrap(
        <div style={{ display: "flex", gap: cardGap, justifyContent: "center", flexWrap: "nowrap" }}>
          {h.map((_, i) => (
            <div key={i}>{renderCardBack(false)}</div>
          ))}
        </div>
      );
    }

    // Sort hand for stable display order (grouped by suit, trump on right)
    const sorted = sortHandForDisplay(h, trump);

    return wrap(
      <div style={{ display: "flex", gap: isMobile ? 2 : 5, justifyContent: "center", flexWrap: "nowrap" }}>
        {sorted.map((c) => (
          <div key={cardKey(c)}>
            {renderCard(c, onClick ? () => onClick(c) : null, false, false)}
          </div>
        ))}
      </div>
    );
  }

  function renderTrickArea() {
    const cardsToShow = trickPauseCards || currentTrick;

    // Display positions by visual slot: 0=bottom, 1=right(3p)/left(4p), 2=top, 3=right(4p)
    const pos3 = [
      { bottom: "10%", left: "50%", transform: "translateX(-50%)" }, // display 0 (bottom)
      { top: "60%", right: "25%", transform: "translateY(-50%)" },  // display 1 (right)
      { top: "10%", left: "50%", transform: "translateX(-50%)" },  // display 2 (top)
    ];
    const pos4 = [
      { bottom: "10%", left: "50%", transform: "translateX(-50%)" }, // display 0 (bottom)
      { top: "60%", left: "25%", transform: "translateY(-50%)" },   // display 1 (left)
      { top: "10%", left: "50%", transform: "translateX(-50%)" },   // display 2 (top)
      { top: "60%", right: "25%", transform: "translateY(-50%)" },  // display 3 (right)
    ];
    const pos = playerCount === 4 ? pos4 : pos3;

    return (cardsToShow || []).map((t, i) => (
      <div key={i} style={{ position: "absolute", ...pos[dSeat(t.player)], zIndex: 10 }}>
        {renderCard(t.card, null, false, false)}
      </div>
    ));
  }

  function renderScorecard() {
    const scorecardHeight = isMultiplayer ? "55vh" : "80vh";

    return (
      <div style={{
        position: "fixed",
        left: 10,
        top: 50,
        width: 290,
        height: isMultiplayer ? "calc(100vh - 70px)" : "85vh",
        zIndex: 20,
        display: "flex",
        flexDirection: "column",
        gap: 8
      }}>
        {/* Scorecard */}
        <div
          style={{
            width: 320,
            height: scorecardHeight,
            background: "#fef9e7",
            borderRadius: 8,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            border: "2px solid #d4af37",
            fontFamily: "'Courier New', monospace",
            fontSize: 15,
            color: "#222",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            position: "relative",
            flexShrink: 0
          }}
        >
          {/* Logo watermark */}
          <img
            src="/BuckEuchreLogo.png"
            alt=""
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: 500,
              opacity: 0.15,
              pointerEvents: "none",
              zIndex: 0
            }}
          />

          {/* Fixed header */}
          <div style={{ padding: "12px 10px 0", position: "relative", zIndex: 1, flexShrink: 0 }}>
            <div style={{ textAlign: "center", fontWeight: 700, marginBottom: 8, fontSize: 17, borderBottom: "1px solid #888", paddingBottom: 4 }}>
              SCORECARD
            </div>
            {/* Column headers */}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, borderBottom: "1px solid #888", paddingBottom: 4, marginBottom: 6, fontSize: playerCount === 4 ? 13 : 14 }}>
              <span style={{ width: `${100/(playerCount+1)}%` }}>Hand</span>
              {ALL_PLAYERS.map(i => (
                <span key={i} style={{ width: `${100/(playerCount+1)}%`, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{playerLabels[i]}</span>
              ))}
            </div>

            {/* Current totals */}
            <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, marginBottom: 6, background: "#e8f4f8", padding: "4px", borderRadius: 4, fontSize: playerCount === 4 ? 14 : 16 }}>
              <span style={{ width: `${100/(playerCount+1)}%` }}>Total</span>
              {ALL_PLAYERS.map(i => (
                <span key={i} style={{ width: `${100/(playerCount+1)}%`, textAlign: "center" }}>{scores[i]}</span>
              ))}
            </div>
          </div>

          {/* Scrollable score history */}
          <div style={{ flex: 1, overflowY: "auto", padding: "0 10px", position: "relative", zIndex: 1, minHeight: 100 }}>
            {[...scoreHistory].reverse().map((sh, i) => {
              // Support both old format (p0/p1/p2) and new format (deltas array)
              const d = sh.deltas || [sh.p0 || 0, sh.p1 || 0, sh.p2 || 0, sh.p3 || 0];
              return (
                <div key={i} style={{ marginBottom: 4 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: playerCount === 4 ? 13 : 15 }}>
                    <span style={{ width: `${100/(playerCount+1)}%` }}>{sh.hand}</span>
                    {ALL_PLAYERS.map(j => (
                      <span key={j} style={{ width: `${100/(playerCount+1)}%`, textAlign: "center", color: d[j] < 0 ? "green" : d[j] > 0 ? "red" : "#666" }}>
                        {d[j] > 0 ? "+" : ""}{d[j] || "—"}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Hand History collapsible section */}
          {handHistory.length > 0 && (
            <div style={{ borderTop: "1px solid #888", background: "#fef3c7", flexShrink: 0 }}>
              <button
                onClick={() => setShowHandHistory(h => !h)}
                style={{
                  width: "100%", background: "none", border: "none", padding: "4px 10px",
                  cursor: "pointer", fontSize: 13, color: "#92400e", fontWeight: 600, textAlign: "left"
                }}
              >
                {showHandHistory ? "▾" : "▸"} Hand History ({handHistory.length})
              </button>
              {showHandHistory && (
                <div style={{ maxHeight: 200, overflowY: "auto", padding: "0 10px 6px" }}>
                  {[...handHistory].reverse().map((h, i) => {
                    const suitColor = s => (s === "♥" || s === "♦") ? "#dc2626" : "#1e293b";
                    return (
                      <details key={i} style={{ marginBottom: 4, fontSize: 13 }}>
                        <summary style={{ cursor: "pointer", color: "#78350f", fontWeight: 500 }}>
                          Hand {h.handNumber}: <span style={{ color: suitColor(h.trump) }}>{h.trump}</span> called by {playerLabels[h.trumpCaller]}
                          {h.outcome === "spades_auto" && " (forced)"}
                        </summary>
                        <div style={{ paddingLeft: 12, color: "#92400e", fontSize: 12 }}>
                          {h.tricks.map((trick, ti) => (
                            <div key={ti} style={{ marginBottom: 2 }}>
                              <strong>Trick {ti + 1}:</strong>{" "}
                              {trick.cards.map((c, ci) => (
                                <span key={ci}>
                                  {playerLabels[c.player]}: <span style={{ color: suitColor(c.card.suit) }}>{c.card.rank}{c.card.suit}</span>
                                  {ci < trick.cards.length - 1 ? " | " : ""}
                                </span>
                              ))}
                              {" → "}<strong>{playerLabels[trick.winner]}</strong>
                            </div>
                          ))}
                        </div>
                      </details>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Last Trick viewer */}
          {allTricks.length > 0 && (
            <div style={{ borderTop: "1px solid #888", background: "#fef3c7", flexShrink: 0 }}>
              <button
                onClick={() => setShowLastTrick(h => !h)}
                style={{
                  width: "100%", background: "none", border: "none", padding: "4px 10px",
                  cursor: "pointer", fontSize: 13, color: "#92400e", fontWeight: 600, textAlign: "left"
                }}
              >
                {showLastTrick ? "▾" : "▸"} Last Trick
              </button>
              {showLastTrick && (() => {
                const lt = allTricks[allTricks.length - 1];
                const suitColor = s => (s === "♥" || s === "♦") ? "#dc2626" : "#1e293b";
                return (
                  <div style={{ padding: "0 10px 8px", fontSize: 13 }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 4 }}>
                      {lt.cards.map((c, ci) => (
                        <span key={ci} style={{ background: "#fff", border: "1px solid #d4af37", borderRadius: 4, padding: "2px 6px", fontSize: 13 }}>
                          <span style={{ color: "#78350f", fontSize: 11 }}>{playerLabels[c.player]}: </span>
                          <span style={{ color: suitColor(c.card.suit), fontWeight: 700 }}>{c.card.rank}{c.card.suit}</span>
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: 12, color: "#78350f" }}>
                      Winner: <strong>{playerLabels[lt.winner]}</strong>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Player action panel inside scorecard */}
          <div style={{
            borderTop: "1px solid #888",
            padding: "8px 10px",
            background: "#fef9e7",
            position: "relative",
            zIndex: 1,
            flexShrink: 0
          }}>
            {/* Action buttons */}
            <div style={{ marginBottom: 8 }}>
              {renderActionPanel()}
            </div>

            {/* Voice chat controls - multiplayer only */}
            {isMultiplayer && socketRef.current && (
              <div style={{ marginBottom: 8 }}>
                <VoiceChat socket={socketRef.current} players={multiplayerPlayers} mySeat={mySeat} />
              </div>
            )}

            {/* AI Coach & Fast Forward toggles — inline to save space */}
            <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 16,
              marginBottom: 8
            }}>
              {/* AI Coach */}
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 11, color: "#555", fontWeight: 600 }}>Coach</span>
                <div
                  onClick={() => setAiCoachEnabled(!aiCoachEnabled)}
                  style={{
                    width: 32,
                    height: 16,
                    borderRadius: 8,
                    background: aiCoachEnabled ? "#3b82f6" : "#9ca3af",
                    cursor: "pointer",
                    position: "relative",
                    transition: "background 0.2s"
                  }}
                >
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: "#fff",
                    position: "absolute",
                    top: 2,
                    left: aiCoachEnabled ? 18 : 2,
                    transition: "left 0.2s",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)"
                  }} />
                </div>
              </div>
              {/* AI Speed Up */}
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 11, color: "#555", fontWeight: 600 }}>Speed</span>
                <div
                  onClick={() => setAiSpeedUp(!aiSpeedUp)}
                  style={{
                    width: 32,
                    height: 16,
                    borderRadius: 8,
                    background: aiSpeedUp ? "#22c55e" : "#9ca3af",
                    cursor: "pointer",
                    position: "relative",
                    transition: "background 0.2s"
                  }}
                >
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: "#fff",
                    position: "absolute",
                    top: 2,
                    left: aiSpeedUp ? 18 : 2,
                    transition: "left 0.2s",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)"
                  }} />
                </div>
              </div>
              {/* Fast Forward */}
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 11, color: "#555", fontWeight: 600 }}>Fast Fwd</span>
                <div
                  onClick={() => setFastForward(!fastForward)}
                  style={{
                    width: 32,
                    height: 16,
                    borderRadius: 8,
                    background: fastForward ? "#f59e0b" : "#9ca3af",
                    cursor: "pointer",
                    position: "relative",
                    transition: "background 0.2s"
                  }}
                >
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    background: "#fff",
                    position: "absolute",
                    top: 2,
                    left: fastForward ? 18 : 2,
                    transition: "left 0.2s",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)"
                  }} />
                </div>
              </div>
            </div>

            {/* Auto Deal toggle with delay selector — host only in multiplayer */}
            {(!isMultiplayer || isHost) && (
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                marginBottom: 8
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 11, color: "#555", fontWeight: 600 }}>Auto Deal</span>
                  <div
                    onClick={() => setAutoDeal(!autoDeal)}
                    style={{
                      width: 32,
                      height: 16,
                      borderRadius: 8,
                      background: autoDeal ? "#7c3aed" : "#9ca3af",
                      cursor: "pointer",
                      position: "relative",
                      transition: "background 0.2s"
                    }}
                  >
                    <div style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: "#fff",
                      position: "absolute",
                      top: 2,
                      left: autoDeal ? 18 : 2,
                      transition: "left 0.2s",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.3)"
                    }} />
                  </div>
                </div>
                {autoDeal && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <select
                      value={autoDealDelay}
                      onChange={(e) => setAutoDealDelay(Number(e.target.value))}
                      style={{
                        fontSize: 11,
                        padding: "1px 4px",
                        borderRadius: 4,
                        border: "1px solid #ccc",
                        background: "#fff",
                        color: "#333",
                        cursor: "pointer"
                      }}
                    >
                      <option value={3}>3s</option>
                      <option value={5}>5s</option>
                      <option value={8}>8s</option>
                      <option value={10}>10s</option>
                      <option value={15}>15s</option>
                    </select>
                    <span style={{ fontSize: 10, color: "#888" }}>delay</span>
                  </div>
                )}
              </div>
            )}

            {/* RP Progress widget */}
            {currentUser && (() => {
              const rp = getRankProgress(currentUser.lifetimeRP ?? 0);
              return (
                <div style={{ borderTop: "1px solid #d0b06040", paddingTop: 8, marginTop: 4, marginBottom: 6 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 3 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#92400e" }}>{rp.rank.name}</span>
                    <span style={{ fontSize: 10, color: "#a16207" }}>{(currentUser.lifetimeRP ?? 0).toLocaleString()} RP</span>
                  </div>
                  <div style={{ background: "#e5e7eb", borderRadius: 3, height: 6, overflow: "hidden", marginBottom: 3 }}>
                    <div style={{
                      width: `${Math.round(rp.progress * 100)}%`,
                      height: "100%",
                      background: "linear-gradient(90deg, #b8860b, #d4af37)",
                      borderRadius: 3
                    }} />
                  </div>
                  {rp.nextRank ? (
                    <div style={{ fontSize: 10, color: "#78350f" }}>
                      {rp.pointsToNext.toLocaleString()} RP → <strong>{rp.nextRank.name}</strong>
                    </div>
                  ) : (
                    <div style={{ fontSize: 10, color: "#d4af37", fontWeight: 700 }}>Max Rank!</div>
                  )}
                </div>
              );
            })()}

            <button
              onClick={async () => {
                const isActive = setupPhase === 'playing' && !['idle', 'gameOver'].includes(phase);
                if (!isActive || humanAbandoned) {
                  if (isMultiplayer) navigate("/community-play");
                  else navigate("/home");
                  return;
                }

                // Multiplayer: voluntary leave — AI takes seat, game continues for others
                if (isMultiplayer) {
                  if (!window.confirm('Leave this game? AI will take your seat for the rest of the game.')) return;
                  leaveRoom();
                  navigate("/community-play");
                  return;
                }

                // Single player: instant server-side finish
                if (!window.confirm('Leave this game? AI will finish instantly (no rating points).')) return;
                try {
                  const players = ALL_PLAYERS.map(i => ({
                    playerIndex: i,
                    isAI: i !== HUMAN && !remoteHumanSeats.includes(i),
                    aiName: (i !== HUMAN && !remoteHumanSeats.includes(i)) ? aiPlayerNames[i - 1] : null,
                    userId: i === HUMAN ? currentUser?.userId : null
                  }));
                  const res = await fetch(`${API_BASE_URL}/games/finish-abandoned`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                      gameId, scores: scoresRef.current, dealer, playerCount, handNumber,
                      wagerMode, players, aiNames: aiPlayerNames,
                      token, buyinDollars, perPointDollars
                    })
                  });
                  const data = await res.json();
                  if (data.success && data.humanPayout && data.humanPayout !== 0) {
                    const bal = currentUser?.playMoneyBalance || 0;
                    updateUser({ playMoneyBalance: bal + data.humanPayout });
                  }
                  if (data.rankPoints !== undefined && data.rankPoints !== null) {
                    updateUser({ rankPoints: data.rankPoints, rankName: data.rankName });
                  }
                  console.log(`⚡ Game finished instantly: winner P${data.winner}, scores [${data.finalScores}]`);
                } catch (err) {
                  console.warn('⚡ Instant finish failed, game abandoned:', err.message);
                }
                abandonPayloadRef.current = null; // already handled, don't fire again on unmount
                navigate("/home");
              }}
              style={{
                width: "100%",
                background: humanAbandoned ? "#059669" : "#4b5563",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                padding: "6px 10px",
                fontSize: 11,
                cursor: "pointer"
              }}
            >
              {humanAbandoned ? "AI Finishing... Go Home" : "Back to Home"}
            </button>
            {currentUser && (
              <button
                onClick={() => {
                  signout();
                  navigate('/');
                }}
                style={{
                  width: "100%",
                  background: "transparent",
                  color: "#9ca3af",
                  border: "1px solid #4b5563",
                  borderRadius: 4,
                  padding: "5px 10px",
                  fontSize: 10,
                  cursor: "pointer",
                  marginTop: 4
                }}
              >
                Sign Out
              </button>
            )}
          </div>
        </div>

        {/* Chat panel - multiplayer only, below scorecard */}
        {isMultiplayer && socketRef.current && (
          <div style={{
            flex: 1,
            minHeight: 0,
            width: 240,
            background: "#161b22",
            border: "1px solid #30363d",
            borderRadius: 8,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden"
          }}>
            <ChatPanel socket={socketRef.current} />
          </div>
        )}
      </div>
    );
  }

  function renderAICommentBubble() {
    if (!aiComment) return null;

    // Position based on visual display slot (after seat rotation)
    // slot 1=left(4p)/right(3p), 2=top, 3=right(4p)
    const displaySlot = dSeat(aiComment.playerIdx);
    const positions = playerCount === 4 ? {
      1: { top: isMobile ? "15%" : "18%", left: isMobile ? "1%" : "2%", transform: "none" },    // left
      2: { top: isMobile ? "1%" : "2%", left: "50%", transform: "translateX(-50%)" },            // top center
      3: { top: isMobile ? "15%" : "18%", right: isMobile ? "1%" : "2%", transform: "none" },   // right
    } : {
      1: { top: isMobile ? "15%" : "18%", right: isMobile ? "1%" : "2%", transform: "none" },   // right
      2: { top: isMobile ? "1%" : "2%", left: "50%", transform: "translateX(-50%)" },            // top center
    };

    const pos = positions[displaySlot];
    if (!pos) return null;

    // Colors based on comment type: pass (red), order (green), name (purple), sit (blue), play (amber)
    const colors = {
      pass: { bg: "#fef2f2", border: "#fca5a5", text: "#dc2626" },
      order: { bg: "#f0fdf4", border: "#86efac", text: "#16a34a" },
      name: { bg: "#faf5ff", border: "#c4b5fd", text: "#7c3aed" },
      sit: { bg: "#f0f9ff", border: "#93c5fd", text: "#2563eb" },
      play: { bg: "#fffbeb", border: "#fcd34d", text: "#b45309" }
    };
    const c = colors[aiComment.type] || colors.pass;

    return (
      <div style={{
        position: "absolute",
        ...pos,
        background: c.bg,
        border: `1.5px solid ${c.border}`,
        borderRadius: isMobile ? 8 : 10,
        padding: isMobile ? "6px 10px" : "8px 14px",
        fontSize: isMobile ? 11 : 13,
        fontStyle: "italic",
        color: "#1e293b",
        zIndex: 25,
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
        maxWidth: isMobile ? 140 : 190,
        textAlign: "center",
        lineHeight: 1.3
      }}>
        <strong style={{ display: "block", marginBottom: 2, color: c.text, fontSize: isMobile ? 10 : 12 }}>
          {playerLabels[aiComment.playerIdx]}:
        </strong>
        "{aiComment.text}"
      </div>
    );
  }

  // ─── INLINE ACTIONS near human player (round1, round2, decide_play) ───────────
  function renderHumanInlineActions() {
    if (whoseTurn !== HUMAN) return null;
    const btnS = (bg) => ({
      background: bg, color: "#fff", border: "none", borderRadius: 8,
      padding: isMobile ? "10px 18px" : "8px 16px",
      fontSize: isMobile ? 15 : 14, fontWeight: 700, cursor: "pointer",
      boxShadow: "0 2px 8px rgba(0,0,0,0.4)", minHeight: 38,
    });
    if (phase === "round1") {
      const isDealerTurn = whoseTurn === dealer;
      const acceptDecision = isDealerTurn ? "pick_up" : "order_up";
      const acceptLabel = isDealerTurn ? "Pick It Up" : "Order It Up";
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, marginTop: 6 }}>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>
            Turned up: <strong style={{ color: SUIT_COLORS[turnedUp?.suit], fontSize: 15 }}>{turnedUp ? cardKey(turnedUp) : "?"}</strong>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => onHumanTrumpRound1(acceptDecision)} style={btnS("#16a34a")}>{acceptLabel}</button>
            <button onClick={() => onHumanTrumpRound1("pass")} style={btnS("#dc2626")}>Pass</button>
          </div>
        </div>
      );
    }
    if (phase === "round2") {
      const available = SUITS.filter(s => s !== turnedUp?.suit);
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, marginTop: 6 }}>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>Call trump:</div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" }}>
            {available.map(s => (
              <button key={s} onClick={() => onHumanTrumpRound2(s)} style={{ ...btnS(SUIT_COLORS[s] === "#dc2626" ? "#b91c1c" : "#334155"), fontSize: 20 }}>{s}</button>
            ))}
            <button onClick={() => onHumanTrumpRound2("pass")} style={btnS("#6b7280")}>Pass</button>
          </div>
        </div>
      );
    }
    if (phase === "decide_play") {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, marginTop: 6 }}>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>
            Trump is <strong style={{ color: SUIT_COLORS[trump], fontSize: 16 }}>{trump}</strong>. Play?
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => onHumanDecidePlay(true)} style={btnS("#16a34a")}>Play</button>
            <button onClick={() => onHumanDecidePlay(false)} style={btnS("#6b7280")}>Sit Out</button>
          </div>
        </div>
      );
    }
    return null;
  }

  function renderActionPanel() {
    // Compact style for scorecard-embedded buttons (larger on mobile for touch)
    const scBtnS = (bg) => ({
      background: bg,
      color: "#fff",
      border: "none",
      borderRadius: isMobile ? 6 : 4,
      padding: isMobile ? "10px 18px" : "5px 10px",
      fontSize: isMobile ? 15 : 11,
      fontWeight: 600,
      cursor: "pointer",
      boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
      minHeight: isMobile ? 40 : undefined,
    });

    if (phase === "findDealer") {
      return (
        <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 11, fontStyle: "italic" }}>
          Finding dealer...
        </div>
      );
    }

    if (phase === "dealing") {
      return (
        <div style={{ textAlign: "center", color: "#94a3b8", fontSize: 11, fontStyle: "italic" }}>
          Dealing cards...
        </div>
      );
    }

    if (phase === "pickup_pause") {
      return (
        <div style={{ textAlign: "center", color: "#2563eb", fontSize: 11, fontStyle: "italic" }}>
          Dealer picking up...
        </div>
      );
    }

    if (phase === "handOver") {
      const isStickDealer = handResult?.outcome === "stick_dealer";
      const canDeal = !isMultiplayer || isHost;
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
          <span style={{ color: isStickDealer ? "#f59e0b" : "#16a34a", fontSize: 12, fontWeight: 600 }}>
            {isStickDealer ? "Everyone passed — dealer loses turn, next player deals" : "Hand complete"}
          </span>
          {canDeal ? (
            autoDeal ? (
              <span style={{ fontSize: 11, color: "#7c3aed", fontStyle: "italic" }}>
                Auto-dealing in {autoDealDelay}s...
              </span>
            ) : (
              <button onClick={nextHand} style={scBtnS("#7c3aed")}>
                Next Hand
              </button>
            )
          ) : (
            <span style={{ fontSize: 11, color: "#888", fontStyle: "italic" }}>
              Waiting for host to deal...
            </span>
          )}
        </div>
      );
    }

    if (trickPauseCards) {
      return (
        <div style={{ textAlign: "center", color: "#2563eb", fontSize: 11, fontStyle: "italic" }}>
          Reviewing trick...
        </div>
      );
    }

    if (whoseTurn !== myPlayerSeat || !["round1", "round2", "discard", "playing", "decide_play"].includes(phase)) {
      return (
        <div style={{ textAlign: "center", color: "#888", fontSize: 11, fontStyle: "italic" }}>
          {whoseTurn !== null && whoseTurn !== myPlayerSeat ? `${playerLabels[whoseTurn]} thinking...` : ""}
        </div>
      );
    }

    if (phase === "round1" && whoseTurn === HUMAN) return null; // shown inline near player
    if (phase === "round1") {
      const isDealerTurn = whoseTurn === dealer;
      const acceptLabel = isDealerTurn ? "Pick it up" : "Order it up";
      const acceptDecision = isDealerTurn ? "pick_up" : "order_up";

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
          {/* AI Coach Suggestion */}
          {aiCoachEnabled && botSuggestion && (
            <div style={{
              background: "linear-gradient(135deg, #1e3a5f 0%, #0f2744 100%)",
              border: "1px solid #3b82f6",
              borderRadius: 6,
              padding: "6px 10px",
              maxWidth: 220,
              textAlign: "center"
            }}>
              <div style={{ color: "#60a5fa", fontSize: 11, fontWeight: 600, marginBottom: 2 }}>
                AI COACH
              </div>
              <div style={{ color: "#e2e8f0", fontSize: 13 }}>
                <strong style={{ color: botSuggestion.action === "pass" ? "#f87171" : "#4ade80" }}>
                  {botSuggestion.action === "pass" ? "Pass" : (isDealerTurn ? "Pick It Up" : "Order It Up")}
                </strong>
                {" — "}{botSuggestion.reasoning}
              </div>
            </div>
          )}

          <div style={{ textAlign: "center", color: "#e2e8f0", fontSize: 14 }}>
            Turned up:{" "}
            <strong style={{ color: turnedUp?.suit === "♠" || turnedUp?.suit === "♣" ? "#94a3b8" : SUIT_COLORS[turnedUp?.suit], fontSize: 18 }}>
              {cardKey(turnedUp)}
            </strong>{" "}
            — {acceptLabel}?
          </div>

          <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => onHumanTrumpRound1(acceptDecision)} style={scBtnS("#16a34a")}>
              {acceptLabel === "Pick it up" ? "Pick It Up" : "Order It Up"}
            </button>
            <button onClick={() => onHumanTrumpRound1("pass")} style={scBtnS("#dc2626")}>
              Pass
            </button>
          </div>
        </div>
      );
    }

    if (phase === "round2" && whoseTurn === HUMAN) return null; // shown inline near player
    if (phase === "round2") {
      const turnedSuit = turnedUp?.suit;
      const available = SUITS.filter((s) => s !== turnedSuit);

      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
          {/* AI Coach Suggestion for Round 2 */}
          {aiCoachEnabled && round2Suggestion && (
            <div style={{
              background: "linear-gradient(135deg, #1e3a5f 0%, #0f2744 100%)",
              border: "1px solid #3b82f6",
              borderRadius: 6,
              padding: "6px 10px",
              maxWidth: 220,
              textAlign: "center"
            }}>
              <div style={{ color: "#60a5fa", fontSize: 11, fontWeight: 600, marginBottom: 2 }}>
                AI COACH
              </div>
              <div style={{ color: "#e2e8f0", fontSize: 13 }}>
                {round2Suggestion.action === "pass" ? (
                  <>
                    <strong style={{ color: "#f87171" }}>Pass</strong>
                    {" — "}{round2Suggestion.reasoning}
                  </>
                ) : (
                  <>
                    Call{" "}
                    <strong style={{ color: SUIT_COLORS[round2Suggestion.action] }}>
                      {round2Suggestion.action}
                    </strong>
                    {" — "}{round2Suggestion.reasoning}
                  </>
                )}
              </div>
            </div>
          )}

          <div style={{ textAlign: "center", color: "#e2e8f0", fontSize: 14 }}>Call trump:</div>

          <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
            {available.map((s) => (
              <button key={s} onClick={() => onHumanTrumpRound2(s)} style={scBtnS("#2563eb")}>
                {s}
              </button>
            ))}
            <button onClick={() => onHumanTrumpRound2("pass")} style={scBtnS("#dc2626")}>
              Pass
            </button>
          </div>
        </div>
      );
    }

    if (phase === "decide_play" && whoseTurn === HUMAN) return null; // shown inline near player
    if (phase === "decide_play") {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "center" }}>
          <div style={{ textAlign: "center", color: "#e2e8f0", fontSize: 14 }}>
            Trump is <strong style={{ color: trump === "♠" || trump === "♣" ? "#94a3b8" : SUIT_COLORS[trump], fontSize: 18 }}>{trump}</strong>. Play?
          </div>
          <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
            <button onClick={() => onHumanDecidePlay(true)} style={scBtnS("#16a34a")}>
              Play
            </button>
            <button onClick={() => onHumanDecidePlay(false)} style={scBtnS("#6b7280")}>
              Sit Out
            </button>
          </div>
        </div>
      );
    }

    if (phase === "discard") {
      return (
        <div style={{ textAlign: "center", color: "#b45309", fontSize: 14, fontWeight: 600 }}>
          Choose a card to discard
        </div>
      );
    }

    if (phase === "playing") {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
          {/* AI Coach Play Suggestion */}
          {aiCoachEnabled && playSuggestion && (
            <div style={{
              background: "linear-gradient(135deg, #1e3a5f 0%, #0f2744 100%)",
              border: "1px solid #3b82f6",
              borderRadius: 6,
              padding: "6px 10px",
              maxWidth: 220,
              textAlign: "center"
            }}>
              <div style={{ color: "#60a5fa", fontSize: 11, fontWeight: 600, marginBottom: 2 }}>
                AI COACH
              </div>
              <div style={{ color: "#e2e8f0", fontSize: 13 }}>
                Play{" "}
                <strong style={{ color: SUIT_COLORS[playSuggestion.card?.suit] || "#4ade80" }}>
                  {playSuggestion.cardKey}
                </strong>
                {" — "}{playSuggestion.reasoning}
              </div>
            </div>
          )}

          <div style={{ color: "#2563eb", fontSize: 12, fontWeight: 600 }}>
            Play a card
          </div>
        </div>
      );
    }

    return null;
  }

  // ─── WAGER SETUP ──────────────────────────────────────────────────────────
  // Non-host live play: skip wager screen, show connecting spinner until game-state arrives
  if (isLivePlay && !isHost && setupPhase === "wager") {
    return (
      <div style={{ minHeight: '100vh', background: '#0d1117', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', border: '4px solid #30363d', borderTopColor: '#d4af37', animation: 'spin 0.9s linear infinite', marginBottom: 8 }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ color: '#e2e8f0', fontSize: 16, fontWeight: 600 }}>Joining the table…</div>
        <div style={{ color: '#484f58', fontSize: 13 }}>Waiting for game state from host</div>
      </div>
    );
  }

  // Spectators skip wager setup entirely — wait for game state
  if (isSpectator && setupPhase === "wager") {
    return (
      <div style={{ minHeight: '100vh', background: '#0d1117', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <SpectatorOverlay spectatorCount={spectatorCount} onLeave={() => { leaveRoom(); navigate('/community'); }} />
        <div style={{ color: '#8b949e', fontSize: 16, marginTop: 40 }}>Connecting to game...</div>
        <div style={{ color: '#484f58', fontSize: 12 }}>Game updates are delayed by 30 seconds</div>
      </div>
    );
  }

  if (setupPhase === "wager") {
    // Balance check: must have enough for the buy-in only (losses are capped at zero)
    const playBalance = currentUser?.playMoneyBalance || 0;
    const canPlayMoney = playBalance >= buyinDollars;

    // Guest game limit
    const guestGamesPlayed = parseInt(localStorage.getItem("buck_euchre_guest_games") || "0", 10);
    const guestLimitReached = isGuest && guestGamesPlayed >= 3;

    const handleWagerSelect = (key) => {
      setBalanceError("");
      if (key === "play" && !canPlayMoney) {
        setBalanceError(`Insufficient Bucks. You need ${buyinDollars} for the buy-in. Balance: ${playBalance.toFixed(0)}`);
        return;
      }
      if (key === "none" && guestLimitReached) {
        setBalanceError("You've played 3 free games! Create an account to keep playing.");
        return;
      }
      setWagerMode(key);
      setSetupPhase("playing");
      setTimeout(() => startGame(), 60);
    };

    return (
      <div style={{ ...fullBg, position: "relative" }}>
        {/* Balance header - only for authenticated users */}
        {currentUser && (
          <div style={{
            position: "absolute",
            top: 16,
            right: 24,
            display: "flex",
            gap: 12,
            alignItems: "center"
          }}>
            <span style={{ color: "#94a3b8", fontSize: 13 }}>{currentUser.nickname}</span>
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              background: "#1a2332",
              padding: "4px 10px",
              borderRadius: 8,
              fontSize: 12,
              fontWeight: 600,
              color: "#fbbf24",
              border: "1px solid #fbbf2440"
            }}>
              <img src="/bucks-icon.svg" alt="" style={{ width: 14, height: 14 }} />
              {playBalance.toLocaleString()}
            </span>
          </div>
        )}

        {/* Logo instead of text title */}
        <img
          src="/BuckEuchreLogoWhite.png"
          alt="Buck Euchre"
          style={{ width: isMobile ? 320 : 500, height: "auto", marginBottom: 24 }}
        />
        {currentUser ? (
          <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 24 }}>
            Playing as: <strong style={{ color: "#60a5fa" }}>{currentUser.nickname}</strong>
          </div>
        ) : (
          <div style={{ color: "#94a3b8", fontSize: 13, marginBottom: 24 }}>
            Playing as: <strong style={{ color: "#a78bfa" }}>Guest</strong>
            <span style={{ color: "#64748b", fontSize: 11, marginLeft: 8 }}>
              ({3 - guestGamesPlayed} free game{3 - guestGamesPlayed !== 1 ? "s" : ""} remaining)
            </span>
          </div>
        )}

        {/* Player count — solo games only; multiplayer uses URL param from room creation */}
        {!isMultiplayer && (
          <div style={{ background: "#1e293b", borderRadius: 12, padding: "16px 28px", marginBottom: 16, maxWidth: 420, width: "100%" }}>
            <div style={{ color: "#e2e8f0", fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Players at the Table</div>
            <div style={{ display: "flex", gap: 10 }}>
              {[3, 4].map(n => (
                <button key={n} onClick={() => navigate(`/play?players=${n}`, { replace: true })}
                  style={{ flex: 1, padding: "10px", borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: "pointer", background: playerCount === n ? "#d4af37" : "#0d1117", color: playerCount === n ? "#000" : "#8b949e", border: playerCount === n ? "2px solid #d4af37" : "1px solid #30363d" }}>
                  {n} Players
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Table Stakes - only for authenticated users */}
        {currentUser && (
          <div style={{ background: "#1e293b", borderRadius: 12, padding: "20px 28px", marginBottom: 24, maxWidth: 420, width: "100%" }}>
            <div style={{ color: "#e2e8f0", fontSize: 14, fontWeight: 700, marginBottom: 14 }}>Table Stakes</div>

            {/* Entry Fee — rank-gated: higher ranks unlock higher stakes */}
            {(() => {
              const userRankPoints = currentUser?.lifetimeRP || currentUser?.rankPoints || 0;
              const userRank = getRankFromPoints(userRankPoints);
              const tierIdx = RANK_TIERS.findIndex(t => t.name === userRank.name);
              // Nine(0)→$2, Ten(1)→$5, Queen(2)→$10, King(3)→$20, Ace+(4+)→$50
              const maxEntryByTier = [2, 5, 10, 20, 50, 50, 50];
              const maxEntry = maxEntryByTier[Math.min(tierIdx, maxEntryByTier.length - 1)];
              // Bucks per Point gates: Nine→0.05, Ten→0.10, Queen→0.25, King+→all
              const maxBppByTier = [0.05, 0.10, 0.25, 0.50, 1.00, 1.00, 1.00];
              const maxBpp = maxBppByTier[Math.min(tierIdx, maxBppByTier.length - 1)];

              return (
                <>
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ color: "#94a3b8", fontSize: 11, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Entry Fee (Bucks)</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {[2, 5, 10, 20, 50].map(amt => {
                        const locked = amt > maxEntry;
                        const selected = buyinDollars === amt;
                        return (
                          <button
                            key={amt}
                            disabled={locked}
                            onClick={() => { if (!locked) { setBuyinDollars(amt); setBalanceError(""); } }}
                            title={locked ? `Unlock at ${RANK_TIERS[maxEntryByTier.indexOf(amt) === -1 ? 4 : [2,5,10,20,50].indexOf(amt)].name} rank` : ""}
                            style={{
                              flex: 1, minWidth: 52, padding: "10px 6px", borderRadius: 8,
                              background: locked ? "#1a1a2e" : selected ? "#d4af37" : "#0d1117",
                              color: locked ? "#4b5563" : selected ? "#0d1117" : "#8b949e",
                              border: locked ? "1px solid #252540" : selected ? "2px solid #d4af37" : "1px solid #30363d",
                              fontSize: 15, fontWeight: 700,
                              cursor: locked ? "not-allowed" : "pointer",
                              opacity: locked ? 0.5 : 1,
                              display: "flex", alignItems: "center", justifyContent: "center", gap: 3
                            }}
                          >
                            {locked ? "🔒" : <img src="/bucks-icon.svg" alt="" style={{ width: 12, height: 12, opacity: selected ? 0.8 : 0.5 }} />}
                            {amt}
                          </button>
                        );
                      })}
                    </div>
                    {tierIdx < 4 && (
                      <div style={{ color: "#64748b", fontSize: 10, marginTop: 4 }}>
                        Rank up to unlock higher stakes — current: {userRank.name} ({userRankPoints} pts)
                      </div>
                    )}
                  </div>

                  {/* Per Point Cost — also rank-gated */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ color: "#94a3b8", fontSize: 11, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Bucks per Point</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      {[
                        { val: 0.05, label: "0.05" },
                        { val: 0.10, label: "0.10" },
                        { val: 0.25, label: "0.25" },
                        { val: 0.50, label: "0.50" },
                        { val: 1.00, label: "1.00" },
                      ].map(opt => {
                        const locked = opt.val > maxBpp;
                        const selected = perPointDollars === opt.val;
                        return (
                          <button
                            key={opt.val}
                            disabled={locked}
                            onClick={() => { if (!locked) { setPerPointDollars(opt.val); setBalanceError(""); } }}
                            style={{
                              flex: 1, minWidth: 52, padding: "10px 6px", borderRadius: 8,
                              background: locked ? "#1a1a2e" : selected ? "#d4af37" : "#0d1117",
                              color: locked ? "#4b5563" : selected ? "#0d1117" : "#8b949e",
                              border: locked ? "1px solid #252540" : selected ? "2px solid #d4af37" : "1px solid #30363d",
                              fontSize: 14, fontWeight: 700,
                              cursor: locked ? "not-allowed" : "pointer",
                              opacity: locked ? 0.5 : 1,
                            }}
                          >
                            {locked ? "🔒 " : ""}{opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              );
            })()}

            {/* Summary */}
            <div style={{ background: "#0d111780", borderRadius: 8, padding: "10px 14px", border: "1px solid #30363d" }}>
              <div style={{ color: "#94a3b8", fontSize: 12, lineHeight: 1.8 }}>
                Entry: <strong style={{ color: "#fbbf24" }}>{buyinDollars}</strong> Bucks
                <span style={{ color: "#4b5563", margin: "0 6px" }}>|</span>
                Per Point: <strong style={{ color: "#fbbf24" }}>{perPointDollars}</strong> Bucks
              </div>
              <div style={{ color: "#64748b", fontSize: 11, marginTop: 2 }}>
                Min to play: <strong style={{ color: "#f59e0b" }}>{buyinDollars}</strong> Bucks (buy-in only)
              </div>
            </div>
          </div>
        )}

        {/* Balance error + Watch Ad recovery */}
        {balanceError && (
          <div style={{ maxWidth: 420, marginBottom: 16 }}>
            <div style={{
              background: "#3d1f1f",
              border: "1px solid #f85149",
              color: "#f85149",
              padding: "10px 16px",
              borderRadius: 6,
              fontSize: 12,
            }}>
              {balanceError}
            </div>
            {currentUser && !isClubMember && (
              <div style={{
                background: "#1c3d2a",
                border: "1px solid #3fb950",
                borderRadius: 6,
                padding: "10px 16px",
                marginTop: 8,
                textAlign: "center"
              }}>
                {adWatchMsg ? (
                  <div style={{ color: adWatchMsg.includes("error") || adWatchMsg.includes("Maximum") ? "#f85149" : "#3fb950", fontSize: 12, fontWeight: 600 }}>
                    {adWatchMsg}
                    {adWatchMsg.includes("Maximum") && (
                      <a href="/fund" style={{ color: "#58a6ff", marginLeft: 8, textDecoration: "none", fontSize: 11 }}>Earn Bucks</a>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={async () => {
                      setAdWatchMsg("Watching...");
                      const result = await claimAdReward(token);
                      if (result.success) {
                        setAdWatchMsg(`+${result.amount} Bucks! (${result.remaining} left today)`);
                        updateUser({ playMoneyBalance: result.newBalance });
                        // Clear balance error if now sufficient
                        setTimeout(() => {
                          setBalanceError("");
                          setAdWatchMsg("");
                        }, 2000);
                      } else {
                        setAdWatchMsg(result.error || "Could not claim reward");
                      }
                    }}
                    style={{
                      background: "transparent",
                      border: "1px solid #3fb950",
                      color: "#3fb950",
                      borderRadius: 6,
                      padding: "8px 20px",
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                      fontFamily: "'Georgia', serif"
                    }}
                  >
                    Watch Ad for Bucks
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Guest limit reached */}
        {guestLimitReached && (
          <div style={{
            background: "#1a1f2e",
            border: "1px solid #f0883e",
            color: "#f0883e",
            padding: "14px 20px",
            borderRadius: 8,
            marginBottom: 20,
            fontSize: 14,
            fontWeight: 600,
            textAlign: "center",
            maxWidth: 420
          }}>
            You've played 3 free games! Create an account to keep playing.
            <button
              onClick={() => navigate("/signin")}
              style={{
                display: "block",
                margin: "10px auto 0",
                background: "#238636",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                padding: "8px 20px",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer"
              }}
            >
              Create Account
            </button>
          </div>
        )}

        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center" }}>
          {[
            ...(currentUser ? [
              { key: "play", label: "Bucks", color: "#d4af37", sub: buyinDollars + " entry + " + perPointDollars + "/pt" },
            ] : []),
            { key: "none", label: "Free Play", color: "#6366f1", sub: "No stakes" },
          ].map((opt) => (
            <button
              key={opt.key}
              onClick={() => handleWagerSelect(opt.key)}
              style={{
                background: "#1e293b",
                border: "2px solid " + opt.color + "55",
                color: "#e2e8f0",
                borderRadius: 12,
                padding: "24px 34px",
                fontSize: 18,
                fontWeight: 700,
                cursor: "pointer",
                transition: "border-color 0.2s, background 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = opt.color;
                e.currentTarget.style.background = "#1e3a5f";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = opt.color + "55";
                e.currentTarget.style.background = "#1e293b";
              }}
            >
              {opt.label}
              <div style={{ fontSize: 11, fontWeight: 400, color: "#64748b", marginTop: 4 }}>{opt.sub}</div>
            </button>
          ))}
        </div>

        <div style={{ display: "flex", gap: 12, marginTop: 24 }}>
          <button
            onClick={() => navigate("/home")}
            style={{
              background: "transparent",
              color: "#58a6ff",
              border: "1px solid #334155",
              borderRadius: 6,
              padding: "8px 16px",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            Back to Home
          </button>
          {currentUser && (
            <button
              onClick={() => {
                signout();
                navigate('/');
              }}
              style={{
                background: "transparent",
                color: "#6b7280",
                border: "1px solid #334155",
                borderRadius: 6,
                padding: "8px 16px",
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              Sign Out
            </button>
          )}
        </div>
      </div>
    );
  }

  // ── GAME OVER — PODIUM SCREEN ──
  if (phase === "gameOver") {
    const winnerIdx = scores.indexOf(Math.min(...scores));
    const loserIdxs = ALL_PLAYERS.filter(i => i !== winnerIdx);
    const payouts = scores.map((s, i) => {
      if (i === winnerIdx) return null;
      return buyinDollars + Math.max(s, 0) * perPointDollars;
    });
    const totalWon = payouts.reduce((sum, p) => sum + (p || 0), 0);

    // Calculate human player's payout
    const humanPayout = winnerIdx === HUMAN ? totalWon : -(payouts[HUMAN] || 0);
    const isHumanWinner = winnerIdx === HUMAN || (isMultiplayer && winnerIdx === myPlayerSeat);
    const loserNames = loserIdxs.map(i => playerLabels[i]).join(" and ");

    // Sort for podium: winner center, losers by score
    const sortedPlayers = [...ALL_PLAYERS].sort((a, b) => scores[a] - scores[b]);

    return (
      <div style={{
        ...fullBg,
        position: "relative",
        overflow: "hidden",
        justifyContent: "center",
      }}>
        {/* Large logo watermark behind everything */}
        <img src="/BuckEuchreLogoWhite.png" alt="" style={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: isMobile ? "95%" : "70%", maxWidth: 700, height: "auto",
          opacity: 0.06, pointerEvents: "none", zIndex: 0,
        }} />

        <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center", width: "100%", maxWidth: 600 }}>
          {/* Congratulations / Condolences message */}
          {humanAbandoned ? (
            <div style={{ color: "#f59e0b", fontSize: isMobile ? 13 : 15, marginBottom: 14, background: "#1e293b", borderRadius: 8, padding: "8px 16px", border: "1px solid #f59e0b", textAlign: "center" }}>
              AI finished this game for you — no rating points awarded
            </div>
          ) : isHumanWinner ? (
            <div style={{ textAlign: "center", marginBottom: isMobile ? 16 : 24 }}>
              <div style={{ fontSize: isMobile ? 28 : 40, fontWeight: 700, color: "#fbbf24", textShadow: "0 2px 12px rgba(251,191,36,0.3)" }}>
                Congratulations!
              </div>
              <div style={{ fontSize: isMobile ? 14 : 18, color: "#94a3b8", marginTop: 6 }}>
                You beat {loserNames}!
              </div>
            </div>
          ) : (
            <div style={{ textAlign: "center", marginBottom: isMobile ? 16 : 24 }}>
              <div style={{ fontSize: isMobile ? 24 : 34, fontWeight: 700, color: "#8b949e" }}>
                Better luck next time!
              </div>
              <div style={{ fontSize: isMobile ? 14 : 18, color: "#64748b", marginTop: 6 }}>
                <strong style={{ color: "#4ade80" }}>{playerLabels[winnerIdx]}</strong> wins the table
              </div>
            </div>
          )}

          {/* Podium */}
          <div style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            gap: isMobile ? 8 : 16,
            marginBottom: isMobile ? 18 : 28,
            width: "100%",
          }}>
            {sortedPlayers.map((pIdx, rank) => {
              const isWinner = pIdx === winnerIdx;
              const podiumHeight = isWinner ? (isMobile ? 140 : 190) : (isMobile ? 100 : 140) - rank * 8;
              const userRankPts = pIdx === HUMAN ? (currentUser?.activeRP || currentUser?.rankPoints || 0) : 0;
              const userSuit = pIdx === HUMAN ? (currentUser?.preferredSuit || '♠') : '♠';
              const userCardRankId = pIdx === HUMAN ? (currentUser?.cardRankId || currentUser?.permanentCardRank) : undefined;
              const userSuitSymbol = pIdx === HUMAN ? (currentUser?.suitSymbol || currentUser?.preferredSuit || '♠') : undefined;

              return (
                <div key={pIdx} style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  flex: isWinner ? 1.3 : 1,
                  maxWidth: isWinner ? 180 : 140,
                }}>
                  {/* Player name above rank badge */}
                  <div style={{
                    fontSize: isWinner ? (isMobile ? 14 : 18) : (isMobile ? 11 : 14),
                    fontWeight: 700,
                    color: isWinner ? "#fbbf24" : "#c9d1d9",
                    marginBottom: 6,
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}>
                    {playerLabels[pIdx]}
                  </div>

                  {/* Rank badge */}
                  <EloBadge
                    cardRankId={pIdx === HUMAN ? userCardRankId : undefined}
                    suitSymbol={pIdx === HUMAN ? userSuitSymbol : undefined}
                    points={pIdx === HUMAN ? userRankPts : 0}
                    rankName={pIdx === HUMAN ? (currentUser?.cardRankName || currentUser?.rankName) : undefined}
                    preferredSuit={userSuit}
                    showPoints={false}
                    size={isWinner ? "lg" : "md"}
                  />

                  {/* Game Score */}
                  <div style={{
                    fontSize: isWinner ? (isMobile ? 20 : 26) : (isMobile ? 15 : 18),
                    fontWeight: 700,
                    color: isWinner ? "#4ade80" : "#e2e8f0",
                    marginTop: 6,
                  }}>
                    {scores[pIdx]} <span style={{ fontSize: '0.7em', fontWeight: 500, opacity: 0.7 }}>score</span>
                  </div>

                  {/* Bucks Payout */}
                  {wagerMode && wagerMode !== "none" && (
                    <div style={{
                      fontSize: isMobile ? 12 : 14,
                      fontWeight: 700,
                      color: isWinner ? "#4ade80" : "#ef4444",
                      marginTop: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                    }}>
                      <img src="/bucks-icon.svg" alt="" style={{ width: 13, height: 13 }} />
                      {isWinner ? "+" + totalWon.toFixed(0) : "-" + (payouts[pIdx] || 0).toFixed(0)}
                      <span style={{ fontSize: 9, color: "#8b949e", fontWeight: 400 }}>Bucks</span>
                    </div>
                  )}
                  {/* Rank Points Earned (human only) */}
                  {pIdx === HUMAN && !isGuest && !humanAbandoned && rpEarned !== null && (
                    <div style={{
                      fontSize: isMobile ? 11 : 13,
                      fontWeight: 700,
                      color: rpEarned > 0 ? "#d4af37" : "#8b949e",
                      marginTop: 3,
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                    }}>
                      {rpEarned > 0 ? "+" : ""}{rpEarned}
                      <span style={{ fontSize: 9, color: "#8b949e", fontWeight: 400 }}>Rank Points</span>
                    </div>
                  )}

                  {/* Podium block */}
                  <div style={{
                    width: "100%",
                    height: podiumHeight,
                    marginTop: 8,
                    borderRadius: "8px 8px 0 0",
                    background: isWinner
                      ? "linear-gradient(180deg, #d4af37 0%, #8b6914 100%)"
                      : "linear-gradient(180deg, #30363d 0%, #1e293b 100%)",
                    border: isWinner ? "2px solid #fbbf24" : "1px solid #30363d",
                    borderBottom: "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: isWinner ? (isMobile ? 32 : 44) : (isMobile ? 22 : 28),
                    fontWeight: 700,
                    color: isWinner ? "#0d1117" : "#4b5563",
                  }}>
                    {rank === 0 ? "1st" : rank === 1 ? "2nd" : rank === 2 ? "3rd" : `${rank + 1}th`}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Wager summary */}
          {wagerMode && wagerMode !== "none" && (
            <div style={{ color: "#94a3b8", fontSize: 12, marginBottom: 14, background: "#1e293b", borderRadius: 8, padding: "9px 16px", border: "1px solid #334155", maxWidth: 420, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, flexWrap: "wrap" }}>
              <span>Entry: <strong style={{ color: "#fbbf24" }}><img src="/bucks-icon.svg" alt="" style={{ width: 11, height: 11, verticalAlign: "-1px" }} /> {buyinDollars}</strong> + <strong style={{ color: "#fbbf24" }}><img src="/bucks-icon.svg" alt="" style={{ width: 11, height: 11, verticalAlign: "-1px" }} /> {perPointDollars}/pt</strong></span>
              <span style={{ color: "#4b5563" }}>|</span>
              <span style={{ color: humanPayout >= 0 ? "#4ade80" : "#ef4444", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 3 }}>
                <img src="/bucks-icon.svg" alt="" style={{ width: 12, height: 12 }} /> {humanPayout >= 0 ? "+" : ""}{humanPayout.toFixed(0)} Bucks
              </span>
            </div>
          )}

          {/* Post-game tipping (multiplayer only) */}
          {isMultiplayer && remoteHumanSeats.length > 0 && (
            <div style={{ marginBottom: 14, textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "#8b949e", marginBottom: 6 }}>Tip a player for great sportsmanship</div>
              <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
                {remoteHumanSeats.map(seat => {
                  const alreadyTipped = tipSent[seat];
                  return (
                    <div key={seat} style={{ position: "relative" }}>
                      <button
                        disabled={alreadyTipped}
                        onClick={() => setTipPicker(tipPicker === seat ? null : seat)}
                        style={{
                          background: alreadyTipped ? "#1c3d2a" : "#1e293b",
                          color: alreadyTipped ? "#3fb950" : "#fbbf24",
                          border: `1px solid ${alreadyTipped ? "#3fb950" : "#334155"}`,
                          borderRadius: 6, padding: "5px 10px", fontSize: 11,
                          cursor: alreadyTipped ? "default" : "pointer", fontWeight: 600,
                        }}
                      >
                        {alreadyTipped ? `Tipped ${playerLabels[seat]}` : `Tip ${playerLabels[seat]}`}
                      </button>
                      {tipPicker === seat && !alreadyTipped && (
                        <div style={{
                          position: "absolute", bottom: "110%", left: "50%", transform: "translateX(-50%)",
                          background: "#161b22", border: "1px solid #30363d", borderRadius: 8,
                          padding: 6, display: "flex", gap: 4, zIndex: 10,
                        }}>
                          {[1, 2, 5, 10].map(amt => (
                            <button key={amt} onClick={async () => {
                              const mp = multiplayerPlayers.find(p => p.seat === seat);
                              if (mp && token) {
                                await sendTip(token, mp.userId, amt, gameId);
                                setTipSent(prev => ({ ...prev, [seat]: amt }));
                                setTipPicker(null);
                              }
                            }} style={{
                              background: "#238636", color: "#fff", border: "none",
                              borderRadius: 4, padding: "4px 8px", fontSize: 10,
                              cursor: "pointer", fontWeight: 600,
                            }}>
                              <img src="/bucks-icon.svg" alt="" style={{ width: 10, height: 10, verticalAlign: "-1px" }} /> {amt}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Guest limit check after game over */}
          {isGuest && wagerMode === "none" && parseInt(localStorage.getItem("buck_euchre_guest_games") || "0", 10) >= 3 && (
            <div style={{
              background: "#1a1f2e",
              border: "1px solid #f0883e",
              color: "#f0883e",
              padding: "12px 20px",
              borderRadius: 8,
              marginBottom: 14,
              fontSize: 13,
              fontWeight: 600,
              textAlign: "center"
            }}>
              You've used all 3 free games! Create an account to keep playing.
            </div>
          )}

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
            {isGuest && parseInt(localStorage.getItem("buck_euchre_guest_games") || "0", 10) >= 3 ? (
              <button onClick={() => navigate("/signin")} style={btnS("#238636")}>Create Account</button>
            ) : (
              <button onClick={() => startGame()} style={btnS("#16a34a")}>Play Again</button>
            )}
            <button onClick={() => { setSetupPhase("wager"); setPhase("idle"); }} style={btnS("#4b5563")}>Change Setup</button>
            <button onClick={() => navigate("/home")} style={btnS("#111827")}>
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── FIND DEALER SCREEN ──
  if (phase === "findDealer" && findDealerState) {
    const { dealt } = findDealerState;
    const dealCardW = (cardSize.w * 0.7) * 2;
    const dealCardH = (cardSize.h * 0.7) * 2;
    return (
      <div style={{ background: "#0d1117", minHeight: "100vh", display: "flex", flexDirection: "column", fontFamily: "'Georgia', serif", userSelect: "none", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
        {/* Large logo watermark behind everything */}
        <img src="/BuckEuchreLogoWhite.png" alt="" style={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
          width: isMobile ? "90%" : "60%", maxWidth: 600, height: "auto",
          opacity: 0.08, pointerEvents: "none", zIndex: 0
        }} />

        <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <h2 style={{ color: "#e2e8f0", marginBottom: 14, fontSize: isMobile ? 20 : 28 }}>First Black Jack Deals</h2>

          {dealerWinBanner && (
            <div style={{ color: "#fbbf24", fontSize: isMobile ? 22 : 28, fontWeight: 700, marginBottom: 18, textAlign: "center" }}>
              {dealerWinBanner}
            </div>
          )}

          <div style={{ display: "flex", gap: isMobile ? 12 : 40, flexWrap: "wrap", justifyContent: "center" }}>
            {/* Order columns to match dealing order so cards go left-to-right */}
            {(playerCount === 3 ? [0, 2, 1] : ALL_PLAYERS).map((pIdx) => (
              <div key={pIdx} style={{ textAlign: "center" }}>
                <div style={{ color: "#cbd5e1", fontSize: isMobile ? 13 : 14, marginBottom: 8, fontWeight: 600 }}>{playerLabels[pIdx]}</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "center", minHeight: dealCardH + 10, width: isMobile ? (dealCardW * 2 + 20) : (dealCardW * 3 + 24) }}>
                  {(dealt[pIdx] || []).slice(-6).map((c, i) => (
                    <div key={i} style={{ width: dealCardW, height: dealCardH, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <div style={{ transform: "scale(2)", transformOrigin: "center" }}>
                        {renderCard(c, null, false, true)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // ─── SPECTATOR: waiting for delayed state ──────────────────────────────────
  if (isSpectator && (setupPhase !== "playing" || phase === "idle")) {
    return (
      <div style={{ minHeight: '100vh', background: '#0d1117', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
        <SpectatorOverlay spectatorCount={spectatorCount} onLeave={() => { leaveRoom(); navigate('/community'); }} />
        <div style={{ color: '#8b949e', fontSize: 16, marginTop: 40 }}>Waiting for game state...</div>
        <div style={{ color: '#484f58', fontSize: 12 }}>Game updates are delayed by 30 seconds</div>
      </div>
    );
  }

  // ─── MAIN TABLE ────────────────────────────────────────────────────────────
  if (setupPhase !== "playing" || phase === "idle") return null;

  const clickMode = trickPauseCards ? null : (phase === "discard" ? "discard" : phase === "playing" ? "play" : null);
  const clickModeFor = (pIdx) => isSpectator ? null : (pIdx === myPlayerSeat ? clickMode : null);

  // Hardwood floor background style - ADJUSTABLE
  const hardwoodFloorStyle = {
    background: `
      repeating-linear-gradient(
        90deg,
        #3d2817 0px,
        #4a3423 2px,
        #5c4033 4px,
        #4a3423 6px,
        #3d2817 8px
      ),
      repeating-linear-gradient(
        0deg,
        transparent 0px,
        transparent 60px,
        rgba(0,0,0,0.15) 60px,
        rgba(0,0,0,0.15) 62px
      ),
      linear-gradient(180deg, #4a3423 0%, #3d2817 50%, #2d1f12 100%)
    `,
    backgroundBlendMode: "overlay, overlay, normal"
  };

  // Landscape phone overlay
  if (isLandscapePhone) {
    return (
      <div style={{ ...fullBg, justifyContent: "center", alignItems: "center", textAlign: "center", padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>&#x1F4F1;</div>
        <div style={{ color: "#d4af37", fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Rotate Your Phone</div>
        <div style={{ color: "#94a3b8", fontSize: 14 }}>Euchre Table plays best in portrait mode.</div>
      </div>
    );
  }

  return (
    <div style={{ ...hardwoodFloorStyle, minHeight: "100vh", display: "flex", flexDirection: "column", fontFamily: "'Georgia', serif", userSelect: "none", ...(isSpectator ? { paddingTop: 40 } : {}) }}>
      {/* SPECTATOR OVERLAY */}
      {isSpectator && (
        <SpectatorOverlay spectatorCount={spectatorCount} onLeave={() => { leaveRoom(); navigate('/community'); }} />
      )}
      {/* TOP BAR - Desktop only */}
      {!isMobile && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 12px", background: "#161b22", borderBottom: "1px solid #30363d", flexWrap: "wrap", gap: 4, height: 30, minHeight: 30 }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ color: "#94a3b8", fontSize: 12 }}>Hand {handNumber}</span>
            {trump && <span style={{ color: SUIT_COLORS[trump], fontSize: 14, fontWeight: 700 }}>Trump: {trump}</span>}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <span style={{ color: wagerMode === "none" ? "#a78bfa" : "#fbbf24", fontSize: 10, background: "#1e293b", borderRadius: 4, padding: "1px 6px", border: "1px solid #334155" }}>
              {wagerMode === "none" ? "Free" : "Bucks"}
            </span>
            {token && !isGuest && !humanAbandoned && (
              <button onClick={() => {
                if (window.confirm("Forfeit this game? You'll lose your buy-in.")) {
                  const gid = gameIdRef.current || gameId;
                  import('./apiClient').then(({ forfeitGame }) => {
                    forfeitGame(token, gid).then(result => {
                      if (result.success) {
                        if (result.playMoneyBalance !== undefined) {
                          updateUser({ playMoneyBalance: result.playMoneyBalance, realMoneyBalance: result.realMoneyBalance });
                        }
                      }
                    });
                  });
                  navigate('/home');
                }
              }} style={{ background: "none", border: "1px solid #7f1d1d", color: "#ef4444", borderRadius: 4, padding: "1px 6px", fontSize: 10, cursor: "pointer" }}>
                Forfeit
              </button>
            )}
            <button onClick={exportLog} style={{ background: "none", border: "1px solid #334155", color: "#6b7280", borderRadius: 4, padding: "1px 6px", fontSize: 10, cursor: "pointer" }}>
              Export
            </button>
          </div>
        </div>
      )}

      {/* Scorecard - hidden on mobile */}
      {!isMobile && renderScorecard()}

      {/* Mobile bottom bar */}
      {isMobile && (
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: "#161b22", borderTop: "2px solid #d4af37",
          zIndex: 30, paddingBottom: "env(safe-area-inset-bottom, 0px)"
        }}>
          {/* Settings drawer — slides in above the bar when open */}
          {showMobileSettings && (
            <div style={{
              background: "#0d1117", borderBottom: "1px solid #30363d",
              padding: "8px 12px", display: "flex", flexWrap: "wrap",
              alignItems: "center", gap: 10, justifyContent: "space-between"
            }}>
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                {/* Speed */}
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>Speed</span>
                  <div onClick={() => setAiSpeedUp(!aiSpeedUp)} style={{ width: 30, height: 16, borderRadius: 8, background: aiSpeedUp ? "#22c55e" : "#4b5563", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
                    <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: aiSpeedUp ? 16 : 2, transition: "left 0.2s" }} />
                  </div>
                </div>
                {/* Fast Fwd */}
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>Fast Fwd</span>
                  <div onClick={() => setFastForward(!fastForward)} style={{ width: 30, height: 16, borderRadius: 8, background: fastForward ? "#f59e0b" : "#4b5563", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
                    <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: fastForward ? 16 : 2, transition: "left 0.2s" }} />
                  </div>
                </div>
                {/* Auto Deal */}
                {(!isMultiplayer || isHost) && (
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>Auto Deal</span>
                    <div onClick={() => setAutoDeal(!autoDeal)} style={{ width: 30, height: 16, borderRadius: 8, background: autoDeal ? "#7c3aed" : "#4b5563", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
                      <div style={{ width: 12, height: 12, borderRadius: "50%", background: "#fff", position: "absolute", top: 2, left: autoDeal ? 16 : 2, transition: "left 0.2s" }} />
                    </div>
                    {autoDeal && (
                      <select value={autoDealDelay} onChange={(e) => setAutoDealDelay(Number(e.target.value))}
                        style={{ fontSize: 11, padding: "1px 4px", borderRadius: 4, border: "1px solid #4b5563", background: "#21262d", color: "#e2e8f0", cursor: "pointer" }}>
                        <option value={3}>3s</option>
                        <option value={5}>5s</option>
                        <option value={8}>8s</option>
                        <option value={10}>10s</option>
                        <option value={15}>15s</option>
                      </select>
                    )}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <span style={{ color: "#94a3b8", fontSize: 11 }}>
                  {handNumber === 1 ? "1st" : handNumber === 2 ? "2nd" : handNumber === 3 ? "3rd" : `${handNumber}th`} Hand
                  {trump ? ` · ` : ""}
                  {trump && <span style={{ color: SUIT_COLORS[trump], fontWeight: 700 }}>{trump}</span>}
                </span>
                <button onClick={() => navigate('/home')} style={{ background: "none", border: "1px solid #334155", color: "#94a3b8", borderRadius: 4, padding: "2px 10px", fontSize: 11, cursor: "pointer" }}>
                  Exit
                </button>
              </div>
            </div>
          )}
          {/* Main bar: scores + ⚙ button */}
          <div style={{ padding: "5px 8px", display: "flex", flexDirection: "column", gap: 4 }}>
            {/* Score row + settings toggle */}
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ display: "flex", flex: 1, gap: 3 }}>
                {ALL_PLAYERS.map(i => (
                  <div key={i} style={{
                    flex: 1, textAlign: "center",
                    background: i === myPlayerSeat ? "#1e3a5f" : "#21262d",
                    borderRadius: 4, padding: "3px 4px"
                  }}>
                    <div style={{ fontSize: 9, color: "#94a3b8", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {playerLabels[i]}
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: "#fbbf24", lineHeight: 1.1 }}>{scores[i]}</div>
                  </div>
                ))}
              </div>
              <button
                onClick={() => setShowMobileSettings(v => !v)}
                style={{
                  background: showMobileSettings ? "#21262d" : "none",
                  border: "1px solid " + (showMobileSettings ? "#d4af37" : "#30363d"),
                  color: showMobileSettings ? "#d4af37" : "#94a3b8",
                  borderRadius: 6, width: 32, height: 32, fontSize: 16,
                  cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center"
                }}
                title="Settings"
              >⚙</button>
            </div>
            {/* Action panel + human inline actions (round1/round2/decide_play) */}
            <div style={{ textAlign: "center" }}>
              {renderActionPanel()}
              {renderHumanInlineActions()}
            </div>
          </div>
        </div>
      )}

      {/* TABLE - Responsive container */}
      <div style={{
        height: isMobile ? "calc(100vh - 80px)" : "calc(100vh - 90px)",
        display: "flex",
        flexDirection: isMobile ? "column" : undefined,
        alignItems: "center",
        justifyContent: isMobile ? "flex-start" : "center",
        position: "relative",
        overflow: "hidden",
        marginLeft: isMobile ? 0 : 260,
        padding: isMobile ? "5px" : 0,
        paddingRight: isMobile ? 65 : 0,
        paddingTop: isMobile ? "8px" : 0,
        gap: isMobile ? 2 : 0
      }}>
        {/* TOP: Player 2 (3p) or Player 2 (4p) - across from bottom */}
        <div style={{
          ...(isMobile ? {} : {
            position: "absolute",
            top: 10,
            left: "50%",
            transform: "translateX(-50%)",
          }),
          textAlign: "center",
          zIndex: 5,
          width: isMobile ? 280 : 400,
          minHeight: isMobile ? 60 : 120,
          display: "flex",
          flexDirection: "column-reverse",
          alignItems: "center",
          flexShrink: 0
        }}>
          <div style={{ flex: "0 0 auto", zIndex: 6 }}>{renderNameplate(seatAtTop, 0)}</div>
          <div style={{ marginBottom: isMobile ? -8 : 4, flex: "0 0 auto" }}>{renderPlayerHand(seatAtTop, clickModeFor(seatAtTop), 0)}</div>
        </div>

        {/* MIDDLE: TABLE WRAPPER - side players positioned relative to table */}
        <div style={{ position: "relative", flexShrink: 0, transform: isMobile ? "translateX(-10px)" : undefined }}>

          {/* LEFT side player: Player 1 (4p) */}
          {playerCount === 4 && (
            <div style={{
              position: "absolute",
              ...(isMobile
                ? { right: "100%", top: "50%", transform: "translateY(-50%)" }
                : { right: "calc(100% + 10px)", top: "50%", transform: "translateY(-50%)" }
              ),
              zIndex: 5,
              width: isMobile ? 100 : 150,
              height: isMobile ? 200 : 380,
              display: "flex",
              flexDirection: "row-reverse",
              alignItems: "center",
              justifyContent: "center",
              gap: 0
            }}>
              <div style={{ flex: "0 0 auto", marginRight: isMobile ? -10 : -10, zIndex: 6 }}>{renderNameplate(seatAtLeft, 90)}</div>
              <div style={{ flex: "0 0 auto", marginRight: isMobile ? -55 : -60 }}>{renderPlayerHand(seatAtLeft, clickModeFor(seatAtLeft), 90)}</div>
            </div>
          )}

          {/* RIGHT side player: Player 1 (3p) or Player 3 (4p) */}
          <div style={{
            position: "absolute",
            ...(isMobile
              ? { left: "100%", top: "50%", transform: "translateY(-50%)" }
              : { left: "calc(100% + 10px)", top: "50%", transform: "translateY(-50%)" }
            ),
            zIndex: 5,
            width: isMobile ? 100 : 150,
            height: isMobile ? 200 : 380,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 0
          }}>
            <>
              <div style={{ flex: "0 0 auto", marginLeft: isMobile ? -10 : -10, zIndex: 6 }}>{renderNameplate(seatAtRight, -90)}</div>
              <div style={{ flex: "0 0 auto", marginLeft: isMobile ? -55 : -60 }}>{renderPlayerHand(seatAtRight, clickModeFor(seatAtRight), -90)}</div>
            </>
          </div>

          {/* FELT TABLE */}
          <div
            style={{
              width: isMobile ? MT_W : 580,
              height: isMobile ? MT_H : 400,
              borderRadius: isMobile ? 18 : 28,
              background: "radial-gradient(ellipse at center, #1b6b45 0%, #14532d 45%, #0f3d20 100%)",
              border: isMobile ? "5px solid #3b2414" : "9px solid #3b2414",
              boxShadow: "inset 0 0 50px rgba(0,0,0,0.5), 0 6px 24px rgba(0,0,0,0.5)",
              position: "relative",
              flexShrink: 0,
              zIndex: 2,
            }}
          >
            <div style={{ position: "absolute", inset: isMobile ? 4 : 7, borderRadius: isMobile ? 14 : 21, border: "1px dashed rgba(255,255,255,0.07)", pointerEvents: "none" }} />

            {/* Watermark logo in center of table */}
            <img
              src="/BuckEuchreLogoWhite.png"
              alt=""
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: isMobile ? (MT_W - 10) * 2 : 450,
                height: "auto",
                opacity: 0.25,
                pointerEvents: "none",
                zIndex: 1
              }}
            />

            {/* Unified crib/trump container */}
            {phase !== "dealing" && renderCribTrumpContainer()}

            {renderTrickArea()}

            {/* AI Comment Bubble — commented out */}
            {/* {renderAICommentBubble()} */}
          </div>
        </div>

        {/* BOTTOM: You - Responsive container */}
        <div style={{
          ...(isMobile ? {} : {
            position: "absolute",
            bottom: 10,
            left: "50%",
            transform: "translateX(-50%)",
          }),
          textAlign: "center",
          zIndex: 5,
          width: isMobile ? 280 : 400,
          minHeight: isMobile ? 60 : 120,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0
        }}>
          <div style={{ flex: "0 0 auto" }}>{renderNameplate(seatAtBottom, 0)}</div>
          {/* On mobile, inline actions are rendered in the scorebar (outside overflow:hidden) */}
          {!isMobile && renderHumanInlineActions()}
          <div style={{ marginTop: 4, flex: "0 0 auto" }}>{renderPlayerHand(seatAtBottom, clickModeFor(seatAtBottom), 0)}</div>
        </div>
      </div>

      {/* Spacer at bottom */}
      <div style={{ height: isMobile ? 0 : 10 }} />
    </div>
  );
}

// ─── STYLES ───────────────────────────────────────────────────────────────────
const fullBg = {
  background: "#0d1117",
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "'Georgia', serif",
};

// eslint-disable-next-line no-unused-vars
const titleStyle = {
  color: "#e2e8f0",
  fontSize: 40,
  margin: "0 0 8px",
  letterSpacing: 3,
  textTransform: "uppercase",
  fontWeight: 300,
};
